// src/pages/LeadDetailPage.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Divider,
  Chip,
  Grid,
  CircularProgress,
  IconButton,
  useMediaQuery,
  TextField,
  Select,
  Menu,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Breadcrumbs,
  Link,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  LEAD_STATUS_DEFINITIONS,
  getLeadStatusColor,
} from "../constants/leadStatuses";
import { formatStateAbbr, normalizeStateValue } from "../constants/usStates";
import { formatPhone, formatPhoneDisplay } from "../utils/formatPhone";
import LeadStatusIcon from "../components/LeadStatusIcon";
import StateSelect from "../components/StateSelect";
import {
  ArrowLeft as LucideArrowLeft,
  Pencil as LucidePencil,
  Map as LucideMap,
  Trash2 as LucideTrash2,
  Phone as LucidePhone,
  Mail as LucideMail,
  MapPin as LucideMapPin,
  House as LucideHouse,
  CalendarDays as LucideCalendarDays,
  UserRound as LucideUserRound,
  RotateCcw as LucideRotateCcw,
  Archive as LucideArchive,
  FileText as LucideFileText,
  X as LucideX,
  Plus as LucidePlus,
  Send as LucideSend,
  CircleCheck as LucideCircleCheck,
  CircleX as LucideCircleX,
  Eye as LucideEye,
  MessageSquare as LucideMessageSquare,
  DollarSign as LucideDollarSign,
  Ruler as LucideRuler,
  Tag as LucideTag,
  Globe2 as LucideGlobe2,
  MoreHorizontal as LucideMoreHorizontal,
  ClipboardClock as LucideClipboardClock,
  LayoutDashboard as LucideLayoutDashboard,
  StickyNote as LucideStickyNote,
  Clock3 as LucideClock3,
  Folder as LucideFolder,
  ShieldCheck as LucideShieldCheck,
  Activity as LucideActivity,
} from "lucide-react";
import API, { paginationNextPath } from "../utils/api";
import { emitLeadUpdate } from "../utils/leadSync";
import { toast } from "react-toastify";
import StatusUpdateDialog from "../components/StatusUpdateDialog";
import ConfirmDialog from "../components/ConfirmDialog";
import RequiredChoiceDialog, { focusRequiredField } from "../components/RequiredChoiceDialog";
import ActivityTimeline from "../components/ActivityTimeline";
import useAuth from "../hooks/useAuth";
import { useGlobalCounts } from "../context/GlobalCountContext";
import { displayLeadCategoryLabel } from "../utils/leadCategoryLabel";
import {
  getLeadCategoryOutlineColor,
  leadCategoryChipSx,
} from "../utils/leadCategoryChip";
import { useLeadDetailReturn } from "../utils/leadDetailNavigation";
import {
  isAssignableRepRole,
  buildAssignedRepOptions,
  formatAssignedRepOptionLabel,
} from "../utils/assignedRepOptions";

const createLucideMuiIcon = (IconComponent) =>
  React.forwardRef(function LucideMuiIcon(
    { sx, fontSize, size, strokeWidth = 2, ...props },
    ref
  ) {
    const resolvedSize =
      size ??
      (fontSize === "small" ? 18 : fontSize === "large" ? 28 : 20);

    return (
      <Box
        ref={ref}
        component={IconComponent}
        size={resolvedSize}
        strokeWidth={strokeWidth}
        sx={{ display: "inline-block", flexShrink: 0, ...sx }}
        {...props}
      />
    );
  });

function ProposalTabStatusIcon({ status }) {
  const key = String(status || "draft").toLowerCase();
  const common = { size: 14, strokeWidth: 2.25, style: { display: "block", flexShrink: 0 } };
  if (key === "sent") return <LucideSend {...common} color="#f59e0b" aria-hidden />;
  if (key === "accepted") return <LucideCircleCheck {...common} color="#16a34a" aria-hidden />;
  if (key === "declined") return <LucideCircleX {...common} color="#dc2626" aria-hidden />;
  return <LucideClipboardClock {...common} color="#9aa3af" aria-hidden />;
}

const ArrowBackIcon = createLucideMuiIcon(LucideArrowLeft);
const EditIcon = createLucideMuiIcon(LucidePencil);
const MapIcon = createLucideMuiIcon(LucideMap);
const DeleteIcon = createLucideMuiIcon(LucideTrash2);
const PhoneIcon = createLucideMuiIcon(LucidePhone);
const EmailIcon = createLucideMuiIcon(LucideMail);
const LocationOnIcon = createLucideMuiIcon(LucideMapPin);
const HomeIcon = createLucideMuiIcon(LucideHouse);
const CalendarTodayIcon = createLucideMuiIcon(LucideCalendarDays);
const PersonIcon = createLucideMuiIcon(LucideUserRound);
const RestoreIcon = createLucideMuiIcon(LucideRotateCcw);
const ArchiveIcon = createLucideMuiIcon(LucideArchive);
const DescriptionIcon = createLucideMuiIcon(LucideFileText);
const CloseIcon = createLucideMuiIcon(LucideX);
const AddIcon = createLucideMuiIcon(LucidePlus);
const SendIcon = createLucideMuiIcon(LucideSend);
const CheckCircleIcon = createLucideMuiIcon(LucideCircleCheck);
const CancelIcon = createLucideMuiIcon(LucideCircleX);
const VisibilityOutlinedIcon = createLucideMuiIcon(LucideEye);
const MessageSquareIcon = createLucideMuiIcon(LucideMessageSquare);
const DollarSignIcon = createLucideMuiIcon(LucideDollarSign);
const RulerIcon = createLucideMuiIcon(LucideRuler);
const TagIcon = createLucideMuiIcon(LucideTag);
const GlobeIcon = createLucideMuiIcon(LucideGlobe2);
const MoreHorizontalIcon = createLucideMuiIcon(LucideMoreHorizontal);
const LayoutDashboardIcon = createLucideMuiIcon(LucideLayoutDashboard);
const StickyNoteIcon = createLucideMuiIcon(LucideStickyNote);
const ClockIcon = createLucideMuiIcon(LucideClock3);
const FolderIcon = createLucideMuiIcon(LucideFolder);
const ShieldCheckIcon = createLucideMuiIcon(LucideShieldCheck);
const ActivityIcon = createLucideMuiIcon(LucideActivity);

function formatLeadTabNoteStamp(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date
    .toLocaleString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })
    .replace(",", "");
}

const ADT_MONTHLY_CHARGE_ORDER = [
  "smart-alarm-com",
  "secure-alarm-com",
  "complete-alarm-com",
  "smart-with-touchscreen",
  "secure-with-touchscreen",
  "complete-with-touchscreen",
  "smart-without-touchscreen",
  "secure-without-touchscreen",
  "complete-without-touchscreen",
];

function monthlyPriceBounds(product) {
  const options = (Array.isArray(product?.adt_price_options) ? product.adt_price_options : [])
    .map((amount) => Number(amount))
    .filter((amount) => Number.isFinite(amount));
  if (!options.length) return null;
  return { min: Math.min(...options), max: Math.max(...options) };
}

function parseAddonAmount(value) {
  const amount = Number(String(value || "").replace(/[$,\s]/g, ""));
  return Number.isFinite(amount) ? amount : null;
}

function activationFeeOptionsFromProfile(rows) {
  const options = [];
  const seen = new Set();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const amount = parseAddonAmount(row);
    if (amount == null || amount <= 0) return;
    const cents = Math.round(amount * 100);
    if (seen.has(cents)) return;
    seen.add(cents);
    options.push({ label: `$${amount.toFixed(2)}`, amount });
  });
  options.sort((a, b) => a.amount - b.amount);
  return options;
}

function monitoringAddonOptionsFromProfile(rows) {
  const options = [];
  const seen = new Set();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const name = String(row?.name || "").trim();
    const price = parseAddonAmount(row?.price);
    if (!name || price == null) return;
    const key = name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    options.push({ name, price });
  });
  if (!seen.has("nest aware plus")) {
    options.unshift({ name: "Nest Aware Plus", price: 7 });
  }
  return options;
}

function compareMonthlyCharges(a, b) {
  const rank = (product) => {
    const index = ADT_MONTHLY_CHARGE_ORDER.indexOf(product?.adt_package_key);
    return index === -1 ? ADT_MONTHLY_CHARGE_ORDER.length : index;
  };
  const diff = rank(a) - rank(b);
  if (diff !== 0) return diff;
  return String(a?.name || "").localeCompare(String(b?.name || ""));
}

export default function LeadDetailPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { from, fromLabel, goBack } = useLeadDetailReturn();
  const cameFromMobileMap = (from || "").includes("mapLead=");
  const { id } = useParams();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user, role, can, isReadOnly, canViewAdditionalNotes } = useAuth();
  const { refreshCounts } = useGlobalCounts();
  
  // Admin users should bypass read-only restrictions
  const isEditDisabled = isReadOnly && role !== "Admin";
  
  // Admin/Sales Director can edit any lead; Managers can edit leads in their territories;
  // Sales Reps can edit their own assigned leads.
  const canEditLead =
    can.editAnyLead || can.editLeadsInOwnTerritory || can.editOwnLeads;
  const canEditAdditionalNotes =
    (role === "Admin" || role === "Sales Director") && !isEditDisabled;
  const canManageLeadCategory = role === "Admin" || role === "Sales Director" || role === "Manager";
  const canDeleteLead =
    (role === "Admin" || role === "Sales Director") && !isReadOnly;

  // ── Header button styles ──────────────────────────────────────────────────
  const headerGhostButtonSx = {
    minHeight: 42,
    borderRadius: "14px",
    px: 1.6,
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.875rem",
    letterSpacing: "-0.01em",
    border: (theme) =>
      theme.palette.mode === "dark"
        ? "1px solid rgba(255,255,255,0.10)"
        : "1px solid rgba(15,23,42,0.08)",
    background: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.05)"
        : "rgba(255,255,255,0.72)",
    color: "text.primary",
    backdropFilter: "blur(10px)",
    boxShadow: (theme) =>
      theme.palette.mode === "dark"
        ? "0 8px 20px rgba(0,0,0,0.24)"
        : "0 8px 20px rgba(15,23,42,0.06)",
    "&:hover": {
      background: (theme) =>
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.08)"
          : "rgba(255,255,255,0.92)",
      transform: "translateY(-1px)",
    },
  };

  const headerOutlineButtonSx = {
    minHeight: 42,
    borderRadius: "14px",
    px: 1.8,
    textTransform: "none",
    fontWeight: 800,
    fontSize: "0.875rem",
    letterSpacing: "-0.01em",
    borderColor: "rgba(37,99,235,0.28)",
    color: "primary.main",
    background: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(37,99,235,0.08)"
        : "rgba(37,99,235,0.04)",
    "&:hover": {
      borderColor: "primary.main",
      background: (theme) =>
        theme.palette.mode === "dark"
          ? "rgba(37,99,235,0.14)"
          : "rgba(37,99,235,0.08)",
      transform: "translateY(-1px)",
    },
  };

  const headerPrimaryButtonSx = {
    minHeight: 42,
    borderRadius: "14px",
    px: 1.9,
    textTransform: "none",
    fontWeight: 800,
    fontSize: "0.875rem",
    letterSpacing: "-0.01em",
    background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
    color: "#fff",
    boxShadow: "0 10px 22px rgba(37,99,235,0.24)",
    "&:hover": {
      background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
      boxShadow: "0 14px 28px rgba(37,99,235,0.34)",
      transform: "translateY(-1px)",
    },
  };
  // ─────────────────────────────────────────────────────────────────────────

  const proposalControlSx = {
    borderRadius: "12px",
    minHeight: 46,
    bgcolor: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(15,23,42,0.82)"
        : "rgba(255,255,255,0.96)",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: (theme) =>
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.2)"
          : "rgba(15,23,42,0.16)",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: (theme) =>
        theme.palette.mode === "dark"
          ? "rgba(96,165,250,0.7)"
          : "rgba(37,99,235,0.5)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "primary.main",
      borderWidth: 1,
    },
    "&.Mui-focused": {
      boxShadow: (theme) =>
        `0 0 0 4px ${alpha(theme.palette.primary.main, 0.16)}`,
    },
  };

  const proposalSelectSx = {
    ...proposalControlSx,
    "& .MuiSelect-select": {
      fontSize: "calc(0.88rem + 1pt)",
      fontWeight: 500,
      py: 1.25,
    },
  };

  const proposalTextFieldSx = {
    "& .MuiOutlinedInput-root": proposalControlSx,
    "& .MuiInputBase-input": {
      fontSize: "calc(0.88rem + 1pt)",
      fontWeight: 500,
    },
  };

  const proposalMenuProps = {
    PaperProps: {
      sx: {
        mt: 0.75,
        borderRadius: 2,
        p: 0.5,
        border: "1px solid",
        borderColor: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.12)"
            : "rgba(15,23,42,0.08)",
        boxShadow: "0 18px 40px rgba(15,23,42,0.18)",
        "& .MuiMenuItem-root": {
          minHeight: 36,
          borderRadius: 1.25,
          my: 0.25,
          fontSize: "calc(0.84rem + 1pt)",
          fontWeight: 500,
          px: 1.25,
          "&:hover": {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
          },
        },
      },
    },
  };

  const STATUS_OPTIONS = LEAD_STATUS_DEFINITIONS.map((d) => ({
    value: d.value,
    icon: d.Icon,
    color: d.color,
    label: d.value,
  }));

  const [lead, setLead] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [headerMenuAnchor, setHeaderMenuAnchor] = useState(null);
  const [editingContact, setEditingContact] = useState(false);
  const [editingLeadDetails, setEditingLeadDetails] = useState(false);
  const [contactData, setContactData] = useState({ first_name: "", last_name: "", email: "", phone: "", assigned_rep: "", status: "", category: "", followUpDate: "", sms_email_opt_in: "", proposal_language: "en" });
  const [leadCategories, setLeadCategories] = useState([]);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesText, setNotesText] = useState("");
  const [markFollowUpComplete, setMarkFollowUpComplete] = useState(false);
  const [editingAdditionalNotes, setEditingAdditionalNotes] = useState(false);
  const [additionalNotesText, setAdditionalNotesText] = useState("");
  const [tabNotes, setTabNotes] = useState([]);
  const [tabNoteDraft, setTabNoteDraft] = useState("");
  const [savingTabNote, setSavingTabNote] = useState(false);
  const [editingAddress, setEditingAddress] = useState(false);
  const [addressDraft, setAddressDraft] = useState({
    address: "",
    city: "",
    state: "",
    zip_code: "",
  });
  const [savingAddress, setSavingAddress] = useState(false);
  const [users, setUsers] = useState([]);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [restoringLead, setRestoringLead] = useState(false);
  
  // Proposal state
  const [leadTab, setLeadTab] = useState("overview");
  const [leadFiles, setLeadFiles] = useState([]);

  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    API.get(`/leads/${id}/files/`)
      .then((res) => {
        if (!cancelled) setLeadFiles(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setLeadFiles([]);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (leadTab !== "files" || !id) return undefined;
    let cancelled = false;
    API.get(`/leads/${id}/files/`)
      .then((res) => {
        if (!cancelled) setLeadFiles(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setLeadFiles([]);
      });
    return () => {
      cancelled = true;
    };
  }, [leadTab, id]);
  const [proposalItems, setProposalItems] = useState([]);
  const [activeProposalId, setActiveProposalId] = useState(null);
  const [activeProposalStatus, setActiveProposalStatus] = useState("draft");
  const [productsCatalog, setProductsCatalog] = useState([]);
  const [packagesCatalog, setPackagesCatalog] = useState([]);
  const [addingProductId, setAddingProductId] = useState("");
  const [selectedProposalPackageName, setSelectedProposalPackageName] = useState("");
  const [replacePackageOpen, setReplacePackageOpen] = useState(false);
  const [pendingReplacementPackage, setPendingReplacementPackage] = useState(null);
  const [replacingPackage, setReplacingPackage] = useState(false);
  const [proposalCategoryFilter, setProposalCategoryFilter] = useState("");
const [monthlyServiceCharge, setMonthlyServiceCharge] = useState(null);
const [selectedMonthlyCharge, setSelectedMonthlyCharge] = useState(null);
const [monthlyServiceLabel, setMonthlyServiceLabel] = useState("");
const [monitoringAddonName, setMonitoringAddonName] = useState("");
const [monitoringAddonCharge, setMonitoringAddonCharge] = useState(null);
const [monitoringAddonOptions, setMonitoringAddonOptions] = useState(() =>
  monitoringAddonOptionsFromProfile([])
);
const [leadProposals, setLeadProposals] = useState([]);

// Quick View (Proposal Preview)
const [quickViewOpen, setQuickViewOpen] = useState(false);
const [quickViewHtml, setQuickViewHtml] = useState(null);
const [quickViewLoading, setQuickViewLoading] = useState(false);
const [quickViewError, setQuickViewError] = useState(null);
const [lastPreviewProposal, setLastPreviewProposal] = useState(null);

// Discount (one-time)
const [proposalDiscount, setProposalDiscount] = useState(0);
const [activationFee, setActivationFee] = useState(null);
const [activationFeeOptions, setActivationFeeOptions] = useState([]);
const [proposalDiscountLabel, setProposalDiscountLabel] = useState("");
const [selectedDiscount, setSelectedDiscount] = useState(null);
const [proposalSpecialTerms, setProposalSpecialTerms] = useState("");
const [proposalBuilderStep, setProposalBuilderStep] = useState(1);
const [proposalEmailRequiredOpen, setProposalEmailRequiredOpen] = useState(false);
const [followUpDateRequiredOpen, setFollowUpDateRequiredOpen] = useState(false);
  const [assignedRepError, setAssignedRepError] = useState(false);
const [proposalSentMapOpen, setProposalSentMapOpen] = useState(false);
const [proposalBuildMode, setProposalBuildMode] = useState("kits");
const [fillQuickConfigured, setFillQuickConfigured] = useState(false);
const [confirmWithdrawOpen, setConfirmWithdrawOpen] = useState(false);
const [withdrawProposalId, setWithdrawProposalId] = useState(null);

const getNormalizedProductType = (item) =>
  String(item?.product_type || "").trim().toLowerCase();

const normalizeCategoryWhitespace = (value) =>
  String(value || "").trim().replace(/\s+/g, " ");

const normalizeCategoryKey = (value) =>
  normalizeCategoryWhitespace(value).toLowerCase();

const getNormalizedCategory = (item) =>
  normalizeCategoryKey(item?.category);

const mapNewProposalItemsToRows = (items = [], status = "draft") =>
  items.map((item) => ({
    id: item.id,
    product: item.product
      ? {
          id: item.product,
          sku: item.sku_snapshot || item.product_sku || "",
        }
      : null,
    product_name: item.name_snapshot || item.product_name || "Unknown Product",
    quantity: item.quantity || 1,
    unit_price: item.unit_price_snapshot ?? 0,
    status,
  }));

const toProposalHistoryRow = (proposal) => {
  const formatDateTime = (iso) => {
    if (!iso) return "Not sent";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "Not sent" : d.toLocaleString();
  };

  const status = String(proposal?.status || "draft").toLowerCase();
  const statusLabel = status.charAt(0).toUpperCase() + status.slice(1);
  const statusColor =
    status === "accepted"
      ? "success"
      : status === "declined"
      ? "error"
      : status === "sent"
      ? "info"
      : status === "withdrawn"
      ? "warning"
      : "default";

  return {
    id: proposal.id,
    proposalId: proposal.id,
    status,
    statusLabel,
    statusColor,
    sentAtLabel: formatDateTime(proposal.sent_at || proposal.created_at),
    totalLabel: `$${parseFloat(proposal.total || 0).toFixed(2)}`,
    toEmail: proposal.to_email || "—",
    createdBy: proposal.created_by_name || "—",
  };
};

const resetProposalBuilderState = () => {
  setProposalItems([]);
  setSelectedProposalPackageName("");
  setMonthlyServiceCharge(null);
  setSelectedMonthlyCharge(null);
  setMonthlyServiceLabel("");
  setMonitoringAddonName("");
  setMonitoringAddonCharge(null);
  setProposalDiscount(0);
  setActivationFee(null);
  setProposalDiscountLabel("");
  setSelectedDiscount(null);
  setProposalSpecialTerms("");
};

const loadLeadProposalsFromNewApi = async (leadId) => {
  const res = await API.get(`/leads/${leadId}/proposals/`);
  const payload = res?.data;
  const proposals = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.results)
      ? payload.results
      : [];
  setLeadProposals(proposals);
  return proposals;
};





const openQuickView = async (proposal) => {
  const proposalId = proposal?.proposalId || proposal?.id;
  if (!proposalId) {
    console.warn("Quick View blocked: missing proposal.id", proposal);
    return;
  }

  setLastPreviewProposal(proposal);
  setQuickViewOpen(true);
  setQuickViewLoading(true);
  setQuickViewError(null);

  try {
    const res = await API.get(`/proposals/${proposalId}/preview/`);
    setQuickViewHtml(res.data);
  } catch {
    setQuickViewError("Failed to load proposal preview.");
  } finally {
    setQuickViewLoading(false);
  }
};



const closeQuickView = () => {
  setQuickViewOpen(false);
  setQuickViewHtml(null);
  setQuickViewError(null);
};

const handleWithdrawProposal = async () => {
  const pid = withdrawProposalId;
  if (!pid || !lead?.id) return;
  try {
    await API.post(`/proposals/${pid}/withdraw/`);
    toast.success("Proposal withdrawn.");
    const proposals = await loadLeadProposalsFromNewApi(lead.id);
    const updated = proposals.find((x) => x.id === pid);
    if (updated && activeProposalId === pid) {
      setActiveProposalStatus(updated.status);
    }
  } catch (err) {
    console.error(err);
    toast.error(
      err?.response?.data?.detail || "Failed to withdraw proposal."
    );
  } finally {
    setConfirmWithdrawOpen(false);
    setWithdrawProposalId(null);
  }
};

const reopenProposalForEdit = async (proposalId) => {
  if (!proposalId || !lead?.id || isEditDisabled || lead?.is_archived) return;
  try {
    await API.post(`/proposals/${proposalId}/reopen/`);
    const proposals = await loadLeadProposalsFromNewApi(lead.id);
    const fresh = proposals.find((p) => p.id === proposalId);
    if (fresh) {
      applyProposalToBuilder(fresh);
      setProposalBuilderStep(1);
    }
    toast.success("Proposal reopened. Send it again when it is ready.");
  } catch (err) {
    console.error(err);
    toast.error(err?.response?.data?.detail || "Failed to edit proposal.");
  }
};

const proposalHistoryViewButtonSx = {
  textTransform: "none",
  fontSize: "0.75rem",
  fontWeight: 600,
  minHeight: 32,
  px: 1.25,
  borderColor: "primary.main",
  color: "primary.main",
  "&:hover": {
    bgcolor: "primary.main",
    color: "primary.contrastText",
    borderColor: "primary.main",
    "& .MuiButton-startIcon": { color: "primary.contrastText" },
  },
};

const proposalHistory = React.useMemo(() => {
  return leadProposals
    .filter((proposal) => lead?.is_archived || proposal.id !== activeProposalId)
    .map(toProposalHistoryRow);
}, [leadProposals, activeProposalId, lead?.is_archived]);


const renderDesktopRows = () => (
  <Box sx={{ display: "flex", flexDirection: "column" }}>
    {proposalHistory.map((item) => (
      <Box
        key={item.id}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          py: 0.75,
          borderBottom: "1px solid",
          borderColor: "divider",
          "&:last-child": { borderBottom: "none" },
        }}
      >
        <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" label={item.statusLabel} color={item.statusColor} />
            <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
              {item.totalLabel}
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: "0.76rem", color: "text.secondary" }}>
            Sent: {item.sentAtLabel} · To: {item.toEmail}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<VisibilityOutlinedIcon fontSize="small" />}
            sx={proposalHistoryViewButtonSx}
            onClick={(e) => {
              e.stopPropagation();
              openQuickView(item);
            }}
          >
            View
          </Button>
          {item.status === "draft" && (
            <Button
              variant="outlined"
              size="small"
              disabled={isEditDisabled || !!lead?.is_archived}
              sx={{ textTransform: "none", fontSize: "0.75rem", minHeight: 32 }}
              onClick={(e) => {
                e.stopPropagation();
                const full = leadProposals.find((p) => p.id === item.proposalId);
                if (full) {
                  applyProposalToBuilder(full);
                  setProposalBuilderStep(1);
                }
              }}
            >
              Continue
            </Button>
          )}
          {item.status === "sent" && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              disabled={isEditDisabled || !!lead?.is_archived}
              startIcon={<CancelIcon fontSize="small" />}
              sx={{ textTransform: "none", fontSize: "0.75rem", minHeight: 32 }}
              onClick={(e) => {
                e.stopPropagation();
                setWithdrawProposalId(item.proposalId);
                setConfirmWithdrawOpen(true);
              }}
            >
              Withdraw
            </Button>
          )}
          {(item.status === "sent" || item.status === "withdrawn") && (
            <Button
              variant="outlined"
              size="small"
              disabled={isEditDisabled || !!lead?.is_archived}
              startIcon={<EditIcon fontSize="small" />}
              sx={{ textTransform: "none", fontSize: "0.75rem", minHeight: 32 }}
              onClick={(e) => {
                e.stopPropagation();
                reopenProposalForEdit(item.proposalId);
              }}
            >
              Edit
            </Button>
          )}
        </Stack>
      </Box>
    ))}
  </Box>
);


const renderMobileRows = () => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
    {proposalHistory.map((item) => (
      <Box
        key={item.id}
        sx={{
          py: 0.75,
          borderBottom: "1px solid",
          borderColor: "divider",
          "&:last-child": { borderBottom: "none" },
        }}
      >
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip size="small" label={item.statusLabel} color={item.statusColor} />
            <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>
              {item.totalLabel}
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: "0.74rem", color: "text.secondary" }}>
            {item.sentAtLabel}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityOutlinedIcon fontSize="small" />}
              sx={proposalHistoryViewButtonSx}
              onClick={(e) => {
                e.stopPropagation();
                openQuickView(item);
              }}
            >
              View
            </Button>
            {item.status === "draft" && (
              <Button
                variant="outlined"
                size="small"
                disabled={isEditDisabled || !!lead?.is_archived}
                sx={{ textTransform: "none", fontSize: "0.75rem", minHeight: 32 }}
                onClick={(e) => {
                  e.stopPropagation();
                  const full = leadProposals.find((p) => p.id === item.proposalId);
                  if (full) {
                    applyProposalToBuilder(full);
                    setProposalBuilderStep(1);
                  }
                }}
              >
                Continue
              </Button>
            )}
            {item.status === "sent" && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                disabled={isEditDisabled || !!lead?.is_archived}
                startIcon={<CancelIcon fontSize="small" />}
                sx={{ textTransform: "none", fontSize: "0.75rem", minHeight: 32 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setWithdrawProposalId(item.proposalId);
                  setConfirmWithdrawOpen(true);
                }}
              >
                Withdraw
              </Button>
            )}
            {(item.status === "sent" || item.status === "withdrawn") && (
              <Button
                variant="outlined"
                size="small"
                disabled={isEditDisabled || !!lead?.is_archived}
                startIcon={<EditIcon fontSize="small" />}
                sx={{ textTransform: "none", fontSize: "0.75rem", minHeight: 32 }}
                onClick={(e) => {
                  e.stopPropagation();
                  reopenProposalForEdit(item.proposalId);
                }}
              >
                Edit
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
    ))}
  </Box>
);

  
  // Compute proposal status from backend data
  const proposalStatus = React.useMemo(() => {
    return activeProposalStatus || "draft";
  }, [activeProposalStatus]);

  const proposalFieldsLocked =
    isEditDisabled || !!lead?.is_archived || proposalStatus !== "draft";

// ---------------- Proposal totals (one-time)
const proposalSubtotal = React.useMemo(() => {
  return proposalItems.reduce(
    (sum, item) =>
      sum + parseFloat(item.unit_price || 0) * (item.quantity || 1),
    0
  );
}, [proposalItems]);

const proposalTotal = React.useMemo(() => {
  const fee = activationFee != null && Number(activationFee) > 0 ? Number(activationFee) : 0;
  return Math.max(
    proposalSubtotal - (proposalDiscount || 0) + fee,
    0
  );
}, [proposalSubtotal, proposalDiscount, activationFee]);

  const proposalMonthlyLineLabel = React.useMemo(() => {
    const fromCatalog = selectedMonthlyCharge?.name?.trim();
    const custom = monthlyServiceLabel?.trim();
    if (fromCatalog) return fromCatalog;
    if (custom) return custom;
    return "Monthly service";
  }, [selectedMonthlyCharge, monthlyServiceLabel]);

  const showProposalMonthlySummaryLine = React.useMemo(() => {
    if (selectedMonthlyCharge) return true;
    if (monthlyServiceLabel?.trim()) return true;
    if (
      monthlyServiceCharge != null &&
      monthlyServiceCharge !== "" &&
      !Number.isNaN(Number(monthlyServiceCharge))
    ) {
      return true;
    }
    return false;
  }, [monthlyServiceCharge, selectedMonthlyCharge, monthlyServiceLabel]);

  const proposalMonthlyLineAmountText = React.useMemo(() => {
    if (
      monthlyServiceCharge == null ||
      monthlyServiceCharge === "" ||
      Number.isNaN(Number(monthlyServiceCharge))
    ) {
      return "—";
    }
    return `$${Number(monthlyServiceCharge).toFixed(2)}/mo`;
  }, [monthlyServiceCharge]);

  const hasMonitoringAddon = Boolean(String(monitoringAddonName || "").trim())
    && monitoringAddonCharge != null
    && !Number.isNaN(Number(monitoringAddonCharge));
  const totalMonthlyMonitoring = (Number(monthlyServiceCharge) || 0) + (hasMonitoringAddon ? Number(monitoringAddonCharge) : 0);
  const activationFeeSelectOptions = [...activationFeeOptions];
  if (activationFee != null && Number(activationFee) > 0) {
    const cents = Math.round(Number(activationFee) * 100);
    if (!activationFeeSelectOptions.some((option) => Math.round(option.amount * 100) === cents)) {
      activationFeeSelectOptions.push({
        label: `$${Number(activationFee).toFixed(2)}`,
        amount: Number(activationFee),
      });
      activationFeeSelectOptions.sort((a, b) => a.amount - b.amount);
    }
  }


  const token = localStorage.getItem("access_token");

  // Status is now edited in Contact Information card

  useEffect(() => {
    if (id) {
      fetchLead();
    }
  }, [id]);

  useEffect(() => {
    const loadFillQuickConfig = async () => {
      try {
        const [companyRes, userRes] = await Promise.all([
          API.get("/company-profile/"),
          API.get("/users/me/"),
        ]);
        const hasCompanyConfig =
          Boolean((companyRes.data?.fillquick_user_id || "").trim()) &&
          Boolean((companyRes.data?.fillquick_api_key || "").trim());
        const hasUserConfig =
          Boolean((userRes.data?.fillquick_user_id || "").trim()) &&
          Boolean(userRes.data?.fillquick_configured || false);
        const hasConfig = hasCompanyConfig || hasUserConfig;
        setFillQuickConfigured(hasConfig);
        setMonitoringAddonOptions(monitoringAddonOptionsFromProfile(companyRes.data?.adt_service_addons));
        setActivationFeeOptions(activationFeeOptionsFromProfile(companyRes.data?.adt_activation_fees));
      } catch {
        setFillQuickConfigured(false);
      }
    };
    loadFillQuickConfig();
  }, []);

  // Fetch users/reps for assignment dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await API.get("/users/");
        // Include assignable reps: Sales + Manager roles (active and inactive).
        // Inactive users stay in state so a current inactive assignee can still display.
        // Managers are intentionally assignable for escalation workflows (e.g. at-risk leads).
        const assignableUsers = (Array.isArray(res.data) ? res.data : []).filter(
          isAssignableRepRole
        );
        setUsers(assignableUsers);
      } catch (err) {
        console.error("Error fetching users:", err);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchLeadCategories = async () => {
      if (!canManageLeadCategory) return;
      try {
        const res = await API.get("/lead-categories/");
        const rows = Array.isArray(res.data) ? res.data : (res.data?.results || []);
        setLeadCategories(rows);
      } catch (err) {
        console.error("Error fetching lead categories:", err);
      }
    };
    fetchLeadCategories();
  }, [canManageLeadCategory]);

  // Load product catalog (once)
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        let nextPath = "/settings/products-services/";
        const collected = [];
        const seen = new Set();

        while (nextPath && !seen.has(nextPath)) {
          seen.add(nextPath);
          const res = await API.get(nextPath);
          const payload = res?.data;

          if (Array.isArray(payload)) {
            collected.push(...payload);
            nextPath = paginationNextPath(res);
            continue;
          }

          const pageRows = Array.isArray(payload?.results) ? payload.results : [];
          collected.push(...pageRows);

          const nextUrl = payload?.next;
          if (!nextUrl) {
            nextPath = null;
          } else {
            // Axios baseURL already points to API root.
            // Keep only the relative path part so this works across environments.
            nextPath = nextUrl.includes("/api/")
              ? nextUrl.split("/api/")[1]
              : nextUrl;
          }
        }

        setProductsCatalog(collected);
      } catch (err) {
        console.error("Failed to load products", err);
      }
    };
    const fetchPackages = async () => {
      try {
        let nextPath = "/settings/product-packages/";
        const collected = [];
        const seen = new Set();
        while (nextPath && !seen.has(nextPath)) {
          seen.add(nextPath);
          const res = await API.get(nextPath);
          const payload = res?.data;
          if (Array.isArray(payload)) {
            collected.push(...payload);
            nextPath = paginationNextPath(res);
            continue;
          }
          const pageRows = Array.isArray(payload?.results) ? payload.results : [];
          collected.push(...pageRows);
          const nextUrl = payload?.next;
          if (!nextUrl) {
            nextPath = null;
          } else {
            nextPath = nextUrl.includes("/api/")
              ? nextUrl.split("/api/")[1]
              : nextUrl;
          }
        }
        setPackagesCatalog(collected.filter((pkg) => pkg.is_active !== false));
      } catch (err) {
        console.error("Failed to load product packages", err);
      }
    };
    fetchProducts();
    fetchPackages();
  }, []);

  useEffect(() => {
    if (!productsCatalog.length || !monthlyServiceLabel) return;
    setSelectedMonthlyCharge((current) => {
      if (current && !String(current.id).startsWith("saved-")) return current;
      const match = productsCatalog.find(
        (product) => String(product.name || "").trim() === monthlyServiceLabel.trim()
      );
      return match || current;
    });
  }, [productsCatalog, monthlyServiceLabel]);

  useEffect(() => {
    setAddingProductId("");
  }, [proposalCategoryFilter]);

  // Monthly charges and discounts: show full catalog for these types only.
  // Do not apply proposalCategoryFilter here — that filter narrows *line items*;
  // discount/monthly rows often use different categories than equipment SKUs, which
  // made these dropdowns look empty when a product category was selected.
  const monthlyChargeProducts = React.useMemo(() => {
    return productsCatalog
      .filter((p) => getNormalizedProductType(p) === "monthly_charge")
      .slice()
      .sort(compareMonthlyCharges);
  }, [productsCatalog]);

  const selectedKit = packagesCatalog.find((pkg) => pkg.name === selectedProposalPackageName);
  const visibleMonthlyCharges = React.useMemo(() => {
    const assignedId = selectedKit?.assigned_monthly_charge;
    if (!assignedId) return monthlyChargeProducts;
    const assigned = monthlyChargeProducts.filter(
      (product) => String(product.id) === String(assignedId)
    );
    return assigned.length ? assigned : monthlyChargeProducts;
  }, [monthlyChargeProducts, selectedKit]);
  const selectedMonthlyBounds = monthlyPriceBounds(selectedMonthlyCharge);
  const selectedMonthlyAmount = Number(
    monthlyServiceCharge ?? selectedMonthlyBounds?.max ?? selectedMonthlyCharge?.price ?? 0
  );
  const monthlyAtMin = selectedMonthlyBounds
    ? selectedMonthlyAmount <= selectedMonthlyBounds.min + 0.001
    : selectedMonthlyAmount <= 0;
  const monthlyAtMax = selectedMonthlyBounds
    ? selectedMonthlyAmount >= selectedMonthlyBounds.max - 0.001
    : false;

  const discountProducts = React.useMemo(() => {
    return productsCatalog.filter(
      (p) => getNormalizedProductType(p) === "discount"
    );
  }, [productsCatalog]);

  const proposalPackagesCategoryKey = React.useMemo(
    () => normalizeCategoryKey("Packages / kits"),
    []
  );

  const isProposalPackagesCategory =
    !!proposalCategoryFilter &&
    normalizeCategoryKey(proposalCategoryFilter) === proposalPackagesCategoryKey;

  // Filter products/services for proposal builder.
  // Legacy safety: if product_type is blank/unknown, treat as a selectable item.
  const proposalProducts = React.useMemo(() => {
    if (isProposalPackagesCategory) {
      return [];
    }
    let filtered = productsCatalog.filter((p) => {
      const normalizedType = getNormalizedProductType(p);
      return normalizedType !== "monthly_charge" && normalizedType !== "discount";
    });
    // Apply category filter if one is selected
    if (proposalCategoryFilter) {
      const selectedCategory = normalizeCategoryKey(proposalCategoryFilter);
      filtered = filtered.filter(
        (p) => getNormalizedCategory(p) === selectedCategory
      );
    }
    return filtered;
  }, [productsCatalog, proposalCategoryFilter, isProposalPackagesCategory]);

  const proposalLineItemCatalogProducts = React.useMemo(
    () =>
      productsCatalog.filter((p) => {
        const t = getNormalizedProductType(p);
        return t !== "monthly_charge" && t !== "discount";
      }),
    [productsCatalog]
  );

  // Category options for proposal builder from real catalog data.
  const proposalCategories = React.useMemo(() => {
    const byKey = new Map();
    proposalLineItemCatalogProducts.forEach((p) => {
      const label = normalizeCategoryWhitespace(p.category);
      if (!label) return;
      const key = normalizeCategoryKey(label);
      if (!byKey.has(key)) {
        byKey.set(key, label);
      }
    });
    if (packagesCatalog.length > 0) {
      byKey.set(proposalPackagesCategoryKey, "Packages / kits");
    }

    return [...byKey.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [
    proposalLineItemCatalogProducts,
    packagesCatalog.length,
    proposalPackagesCategoryKey,
  ]);




const handleMonthlySelect = (e) => {
  const value = e.target.value;

  // Empty = custom monthly amount / name
  if (!value) {
    setSelectedMonthlyCharge(null);
    return;
  }

  const selected = monthlyChargeProducts.find(
    (p) => String(p.id) === String(value)
  );

  setSelectedMonthlyCharge(selected || null);

  if (selected?.name) {
    setMonthlyServiceLabel(selected.name);
  }
  const bounds = monthlyPriceBounds(selected);
  if (bounds) {
    setMonthlyServiceCharge(bounds.max);
  } else if (selected?.price) {
    setMonthlyServiceCharge(Number(selected.price));
  }
};

// After catalog loads, re-link draft monthly label to a catalog product for the dropdown.
useEffect(() => {
  if (!monthlyServiceLabel || selectedMonthlyCharge) return;
  const match = monthlyChargeProducts.find(
    (p) => p.name === monthlyServiceLabel
  );
  if (match) setSelectedMonthlyCharge(match);
}, [monthlyChargeProducts, monthlyServiceLabel, selectedMonthlyCharge]);

const handleDiscountSelect = (e) => {
  const value = e.target.value;

  // Empty = custom discount amount
  if (!value) {
    setSelectedDiscount(null);
    setProposalDiscountLabel("");
    setProposalDiscount(0);
    return;
  }

  const selected = discountProducts.find(
    (p) => String(p.id) === String(value)
  );

  setSelectedDiscount(selected || null);
  setProposalDiscountLabel(selected?.name || "");
  // Optionally set default amount from discount price if available
  if (selected?.price) {
    setProposalDiscount(Number(selected.price));
  }
};

  const monthlySelectionFromProposal = (proposal) => {
    const label = String(proposal?.monthly_service_label || "").trim();
    const hasAmount =
      proposal?.monthly_service_charge != null && proposal?.monthly_service_charge !== "";
    const amount = hasAmount ? Number(proposal.monthly_service_charge) : null;
    const match = productsCatalog.find(
      (product) => label && String(product.name || "").trim() === label
    );
    if (match) return match;
    if (label || amount != null) {
      return {
        id: `saved-${proposal.id}`,
        name: label || "Monthly Monitoring Charge",
        price: amount ?? 0,
      };
    }
    return null;
  };

  const applyProposalToBuilder = (proposal) => {
    if (!proposal) {
      setActiveProposalId(null);
      setActiveProposalStatus("draft");
      resetProposalBuilderState();
      setProposalBuilderStep(1);
      return;
    }
    const status = proposal.status || "draft";
    setActiveProposalId(proposal.id);
    setActiveProposalStatus(status);
    setProposalItems(mapNewProposalItemsToRows(proposal.items || [], status));
    setProposalDiscount(Number(proposal.discount_amount || 0));
    setProposalDiscountLabel(String(proposal.discount_label || "").trim());
    setActivationFee(
      proposal.activation_fee != null && proposal.activation_fee !== "" && Number(proposal.activation_fee) > 0
        ? Number(proposal.activation_fee)
        : null
    );
    setSelectedDiscount(null);
    setMonthlyServiceCharge(
      proposal.monthly_service_charge != null && proposal.monthly_service_charge !== ""
        ? Number(proposal.monthly_service_charge)
        : null
    );
    setMonthlyServiceLabel(String(proposal.monthly_service_label || "").trim());
    const addonName = String(proposal.monitoring_addon_name || "").trim();
    setMonitoringAddonName(addonName);
    setMonitoringAddonCharge(
      addonName && proposal.monitoring_addon_charge != null && proposal.monitoring_addon_charge !== ""
        ? Number(proposal.monitoring_addon_charge)
        : null
    );
    setSelectedMonthlyCharge(monthlySelectionFromProposal(proposal));
    setProposalSpecialTerms(String(proposal.special_terms || "").trim());
    setSelectedProposalPackageName("");
    setProposalBuilderStep(1);
  };

  const beginNewProposal = () => {
    setActiveProposalId(null);
    setActiveProposalStatus("draft");
    resetProposalBuilderState();
    setProposalBuilderStep(1);
  };

  const fetchLead = async () => {
    setLoading(true);
    try {
      const res = await API.get(`/leads/${id}/`);
      setLead(res.data);
      // Follow-ups are included in the lead response
      setFollowUps(res.data.followups || []);
      // Initialize contact data
      setContactData({
        first_name: res.data.first_name || "",
        last_name: res.data.last_name || "",
        email: res.data.email || "",
        phone: formatPhone(res.data.phone),
        assigned_rep: res.data.assigned_rep?.id || res.data.assigned_rep || "",
        status: res.data.status || "",
        category: res.data.category || "",
        followUpDate: "",
        sms_email_opt_in: res.data.sms_email_opt_in ?? "",
        proposal_language: res.data.proposal_language === "es" ? "es" : "en",
      });
      // Initialize notes
      setNotesText(res.data.notes || "");
      setAdditionalNotesText(res.data.additional_notes || "");
      try {
        const notesRes = await API.get(`/leads/${id}/tab-notes/`);
        setTabNotes(Array.isArray(notesRes.data) ? notesRes.data : []);
      } catch (notesErr) {
        console.error("Failed to load tab notes:", notesErr);
        setTabNotes([]);
      }
      setTabNoteDraft("");
      const proposals = await loadLeadProposalsFromNewApi(id);
      const draftProposal = proposals.find((p) => p.status === "draft");
      applyProposalToBuilder(draftProposal || proposals[0] || null);
    } catch (err) {
      console.error("Error fetching lead:", err);
      toast.error("Failed to load lead details");
      goBack();
    } finally {
      setLoading(false);
    }
  };

  const ensureDraftProposalForNewFlow = async () => {
    if (!lead?.id) return null;
    if (activeProposalId && activeProposalStatus === "draft") return activeProposalId;

    try {
      const res = await API.post(`/leads/${lead.id}/proposals/`, {
        to_email: lead.email || null,
      });
      const created = res?.data;
      setActiveProposalId(created?.id || null);
      setActiveProposalStatus(created?.status || "draft");
      if (created?.id) {
        setLeadProposals((prev) => [created, ...prev.filter((p) => p.id !== created.id)]);
      }
      return created?.id || null;
    } catch (err) {
      setActiveProposalId(null);
      setActiveProposalStatus("draft");
      return null;
    }
  };

  const updateProposalItemQuantity = async (itemId, newQty) => {
    if (proposalFieldsLocked) return;
    if (!activeProposalId) {
      toast.error("No active draft proposal found.");
      return;
    }
    await API.patch(`/proposals/${activeProposalId}/items/${itemId}/`, {
      quantity: newQty,
    });
    setProposalItems((prev) =>
      prev.map((p) =>
        p.id === itemId
          ? { ...p, quantity: newQty }
          : p
      )
    );
  };

  const removeProposalItem = async (itemId) => {
    if (proposalFieldsLocked) return;
    if (!activeProposalId) {
      toast.error("No active draft proposal found.");
      return;
    }
    await API.delete(`/proposals/${activeProposalId}/items/${itemId}/`);
    setProposalItems((prev) => prev.filter((p) => p.id !== itemId));
  };

  const applyKitMonthly = (pkg) => {
    const assignedId = pkg?.assigned_monthly_charge;
    if (!assignedId) return;
    const product = productsCatalog.find(
      (item) =>
        String(item.id) === String(assignedId) &&
        getNormalizedProductType(item) === "monthly_charge"
    );
    if (!product) return;
    const bounds = monthlyPriceBounds(product);
    setSelectedMonthlyCharge(product);
    setMonthlyServiceLabel(product.name || "");
    setMonthlyServiceCharge(bounds ? bounds.max : Number(product.price || 0));
  };

  const persistPackageToProposal = async (pkg, draftId) => {
    const response = await API.post(`/proposals/${draftId}/add-package/`, {
      product_package: pkg.id,
    });
    const created = Array.isArray(response.data) ? response.data : [];
    if (!created.length) {
      throw new Error("No lines were added from this package.");
    }

    const rows = created.map((newItem) => {
      const pid = newItem.product;
      const catProduct = productsCatalog.find((p) => p.id === pid);
      return {
        id: newItem.id,
        product: {
          id: pid,
          name: catProduct?.name,
          sku: newItem.sku_snapshot || catProduct?.sku || "",
        },
        product_name:
          newItem.name_snapshot || catProduct?.name || "Unknown Product",
        quantity: newItem.quantity || 1,
        unit_price:
          newItem.unit_price_snapshot ?? catProduct?.price ?? 0,
        status: activeProposalStatus || "draft",
      };
    });

    return rows;
  };

  const addPackageToProposal = async (pkg) => {
    if (proposalFieldsLocked || !pkg) return;

    // The currently selected kit is already the active foundation.
    if (selectedProposalPackageName === pkg.name) return;

    // Once equipment exists, choosing another kit is a replacement action.
    // Confirmation is handled on-screen; nothing is changed until confirmed.
    if (proposalItems.length > 0) {
      setPendingReplacementPackage(pkg);
      setReplacePackageOpen(true);
      return;
    }

    const draftId = await ensureDraftProposalForNewFlow();
    if (!draftId) {
      toast.error("Unable to create draft proposal.");
      return;
    }

    try {
      const rows = await persistPackageToProposal(pkg, draftId);
      setProposalItems(rows);
      setSelectedProposalPackageName(pkg.name || "");
      applyKitMonthly(pkg);
    } catch (err) {
      console.error("Error adding package to proposal:", err);
      toast.error(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to add package"
      );
    }
  };

  const closeReplacePackageDialog = () => {
    if (replacingPackage) return;
    setReplacePackageOpen(false);
    setPendingReplacementPackage(null);
  };

  const confirmReplacePackage = async () => {
    const pkg = pendingReplacementPackage;
    if (!pkg || proposalFieldsLocked || replacingPackage) return;

    const draftId = await ensureDraftProposalForNewFlow();
    if (!draftId) {
      toast.error("Unable to create draft proposal.");
      return;
    }

    setReplacingPackage(true);
    try {
      // A kit is the foundation of Step 1. Replacing it intentionally clears
      // the entire equipment build, including individually added products.
      await Promise.all(
        proposalItems
          .filter((item) => item?.id)
          .map((item) =>
            API.delete(`/proposals/${draftId}/items/${item.id}/`)
          )
      );

      const rows = await persistPackageToProposal(pkg, draftId);
      setProposalItems(rows);
      setSelectedProposalPackageName(pkg.name || "");
      applyKitMonthly(pkg);
      setReplacePackageOpen(false);
      setPendingReplacementPackage(null);
    } catch (err) {
      console.error("Error replacing proposal package:", err);
      toast.error(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to replace package"
      );
    } finally {
      setReplacingPackage(false);
    }
  };

  const addProductToProposal = async (selectedProduct) => {
    if (proposalFieldsLocked || !selectedProduct) return;
    const existing = proposalItems.find(
      (p) => p.product === selectedProduct.id || p.product?.id === selectedProduct.id
    );
    if (existing) {
      toast.warning("Product already in proposal");
      return;
    }
    const draftId = await ensureDraftProposalForNewFlow();
    if (!draftId) {
      toast.error("Unable to create draft proposal.");
      return;
    }
    try {
      const response = await API.post(`/proposals/${draftId}/items/`, {
        product: selectedProduct.id,
        quantity: 1,
        unit_price: selectedProduct.price || "0.00",
      });
      const newItem = response.data;
      setProposalItems((prev) => [...prev, {
        id: newItem.id,
        product: {
          id: selectedProduct.id,
          name: selectedProduct.name,
          sku: newItem.sku_snapshot || selectedProduct.sku,
          price: selectedProduct.price,
        },
        product_name: newItem.name_snapshot || selectedProduct.name,
        quantity: newItem.quantity || 1,
        unit_price: newItem.unit_price_snapshot || selectedProduct.price,
        status: activeProposalStatus || "draft",
      }]);
      setAddingProductId("");
      toast.success("Product added to proposal");
    } catch (err) {
      console.error("Error adding product to proposal:", err);
      toast.error(err?.response?.data?.detail || err?.response?.data?.error || "Failed to add product");
    }
  };

  const adjustMonthlyServiceCharge = (delta) => {
    if (proposalFieldsLocked) return;
    setMonthlyServiceCharge((current) => {
      const bounds = monthlyPriceBounds(selectedMonthlyCharge);
      const fallback = bounds ? bounds.max : Number(selectedMonthlyCharge?.price ?? 0);
      const base = Number(current ?? fallback);
      const next = Number((base + delta).toFixed(2));
      if (!bounds) return Math.max(0, next);
      return Math.min(bounds.max, Math.max(bounds.min, next));
    });
  };

  const saveProposalDraft = async () => {
    if (proposalFieldsLocked) return;
    const draftId = await ensureDraftProposalForNewFlow();
    if (!draftId) {
      toast.error("Unable to create draft proposal.");
      return false;
    }
    await API.post(`/proposals/${draftId}/save-draft/`, {
      to_email: lead?.email || null,
      monthly_service_charge: monthlyServiceCharge,
      monthly_service_name:
        selectedMonthlyCharge?.name ||
        monthlyServiceLabel ||
        "Monthly Monitoring Charge",
      monitoring_addon_name: monitoringAddonName || "",
      monitoring_addon_charge: monitoringAddonName ? monitoringAddonCharge : null,
      activation_fee: activationFee != null && Number(activationFee) > 0 ? Number(activationFee) : null,
      proposal_discount: proposalDiscount || 0,
      discount_name: selectedDiscount?.name || "Discount",
      message: "",
      special_terms: proposalSpecialTerms || "",
    });
    toast.success("Draft proposal saved.");
    return true;
  };

  const handleAddProposalEmail = () => {
    setProposalEmailRequiredOpen(false);
    handleContactEdit();
    window.requestAnimationFrame(() => {
      document
        .querySelector('[data-tour="lead-detail-contact-info"]')
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const handleContactEdit = () => {
    setEditingLeadDetails(false);
    setEditingContact(true);
    // Check for existing incomplete follow-up
    const incompleteFollowUp = followUps?.find(f => !f.completed);
    setContactData({
      first_name: lead.first_name || "",
      last_name: lead.last_name || "",
      email: lead.email || "",
      phone: formatPhone(lead.phone),
      assigned_rep: lead.assigned_rep?.id || lead.assigned_rep || "",
      status: lead.status || "",
      category: lead.category || "",
      followUpDate: incompleteFollowUp?.due_date ? new Date(incompleteFollowUp.due_date).toISOString().split('T')[0] : "",
      sms_email_opt_in: lead.sms_email_opt_in ?? "",
      proposal_language: lead.proposal_language === "es" ? "es" : "en",
    });
  };

  const handleLeadDetailsEdit = () => {
    setEditingContact(false);
    setEditingLeadDetails(true);
    setAssignedRepError(false);
    const incompleteFollowUp = followUps?.find(f => !f.completed);
    setContactData({
      first_name: lead.first_name || "",
      last_name: lead.last_name || "",
      email: lead.email || "",
      phone: formatPhone(lead.phone),
      assigned_rep: lead.assigned_rep?.id || lead.assigned_rep || "",
      status: lead.status || "",
      category: lead.category || "",
      followUpDate: incompleteFollowUp?.due_date
        ? new Date(incompleteFollowUp.due_date).toISOString().split("T")[0]
        : "",
      sms_email_opt_in: lead.sms_email_opt_in ?? "",
      proposal_language: lead.proposal_language === "es" ? "es" : "en",
    });
  };

  const handleLeadDetailsCancel = () => {
    setEditingLeadDetails(false);
    setAssignedRepError(false);
    setContactData({
      first_name: lead.first_name || "",
      last_name: lead.last_name || "",
      email: lead.email || "",
      phone: formatPhone(lead.phone),
      assigned_rep: lead.assigned_rep?.id || lead.assigned_rep || "",
      status: lead.status || "",
      category: lead.category || "",
      followUpDate: "",
      sms_email_opt_in: lead.sms_email_opt_in ?? "",
      proposal_language: lead.proposal_language === "es" ? "es" : "en",
    });
  };

  const handleLeadDetailsSave = async () => {
    const rep = contactData.assigned_rep;
    const hasAssignedRep =
      rep != null &&
      rep !== "" &&
      !(typeof rep === "string" && rep.trim() === "");
    if (contactData.status === "Closed Won" && !hasAssignedRep) {
      setAssignedRepError(true);
      return;
    }
    setAssignedRepError(false);
    await handleContactSave();
    setEditingLeadDetails(false);
  };

  const handleContactCancel = () => {
    setEditingContact(false);
    setContactData({
      first_name: lead.first_name || "",
      last_name: lead.last_name || "",
      email: lead.email || "",
      phone: formatPhone(lead.phone),
      assigned_rep: lead.assigned_rep?.id || lead.assigned_rep || "",
      status: lead.status || "",
      category: lead.category || "",
      followUpDate: "",
      sms_email_opt_in: lead.sms_email_opt_in ?? "",
      proposal_language: lead.proposal_language === "es" ? "es" : "en",
    });
  };

  const handleContactSave = async () => {
    if (!lead) return;

    // Validate follow-up date if Follow Up Needed is selected
    if (contactData.status === "Follow Up Needed" && !contactData.followUpDate) {
      setFollowUpDateRequiredOpen(true);
      return;
    }

    try {
      // Extract assigned_rep ID - handle both object, ID, string, and empty string formats
      let assignedRepId = null;
      if (contactData.assigned_rep) {
        if (typeof contactData.assigned_rep === 'object' && contactData.assigned_rep !== null && contactData.assigned_rep.id) {
          assignedRepId = contactData.assigned_rep.id;
        } else if (typeof contactData.assigned_rep === 'number') {
          assignedRepId = contactData.assigned_rep;
        } else if (typeof contactData.assigned_rep === 'string' && contactData.assigned_rep.trim() !== '') {
          const parsed = parseInt(contactData.assigned_rep, 10);
          if (!isNaN(parsed)) {
            assignedRepId = parsed;
          }
        }
      }

      const payload = {
        first_name: (contactData.first_name || "").trim(),
        last_name: (contactData.last_name || "").trim(),
        email: contactData.email || null,
        phone: formatPhone(contactData.phone) || null,
        status: contactData.status,
        notes: lead.notes || "",
        assigned_rep: assignedRepId || null,
        territory: lead.territory || null,
        sms_email_opt_in: contactData.sms_email_opt_in || null,
        proposal_language: contactData.proposal_language === "es" ? "es" : "en",
      };
      if (canManageLeadCategory) {
        payload.category = contactData.category ? Number(contactData.category) : null;
      }

      const res = await API.patch(`/leads/${lead.id}/`, payload);
      setLead(res.data);
      setFollowUps(res.data.followups || []);
      emitLeadUpdate(res.data);
      
      // If Follow Up Needed and date provided, create/update follow-up
      if (contactData.status === "Follow Up Needed" && contactData.followUpDate) {
        try {
          // Check for existing incomplete follow-up from response (not stale state)
          const followupsFromResponse = res.data.followups || [];
          const incompleteFollowUp = followupsFromResponse.find(f => !f.completed);
          
          if (incompleteFollowUp) {
            // UPDATE existing follow-up
            const isInitialSchedule = !incompleteFollowUp.due_date;
            await API.patch(`/followups/${incompleteFollowUp.id}/`, {
              due_date: contactData.followUpDate,
              notes: isInitialSchedule
                ? `Follow-up scheduled for ${contactData.followUpDate}`
                : `Follow-up rescheduled to ${contactData.followUpDate}`,
            });
            toast.success("Follow-up date updated!");
          } else {
            // CREATE new follow-up
            await API.post("/followups/", {
              lead: lead.id,
              due_date: contactData.followUpDate,
              completed: false,
              notes: `Follow-up scheduled for ${contactData.followUpDate}`,
            });
            toast.success("Follow-up created!");
          }
          
          // Refresh lead data to get updated follow-ups
          await fetchLead();
        } catch (followUpErr) {
          console.error("Failed to create/update follow-up:", followUpErr);
          // Don't fail the whole save if follow-up operation fails
          toast.warning("Contact updated but failed to update follow-up");
        }
      }
      
      setEditingContact(false);
      toast.success("Contact information updated successfully!");
    } catch (err) {
      console.error("Contact update failed:", err);
      const errorMsg = err.response?.data?.detail || err.response?.data?.error || "Failed to update contact information";
      toast.error(errorMsg);
    }
  };

  const handleNotesEdit = () => {
    setEditingNotes(true);
    setNotesText(lead.notes || "");
    setMarkFollowUpComplete(false);
  };

  const handleNotesCancel = () => {
    setEditingNotes(false);
    setNotesText(lead.notes || "");
    setMarkFollowUpComplete(false);
  };

  const handleNotesSave = async () => {
    if (!lead) return;

    try {
      const payload = {
        notes: notesText || "",
      };

      const res = await API.patch(`/leads/${lead.id}/`, payload);
      setLead(res.data);
      emitLeadUpdate(res.data);

      if (markFollowUpComplete) {
        const incompleteFollowUp = followUps?.find((f) => !f.completed);
        if (incompleteFollowUp) {
          try {
            await API.patch(`/followups/${incompleteFollowUp.id}/`, { completed: true });
            const refetch = await API.get(`/leads/${lead.id}/`);
            setLead(refetch.data);
            setFollowUps(refetch.data.followups || []);
            emitLeadUpdate(refetch.data);
            refreshCounts();
            toast.success("Notes updated and follow-up marked complete!");
          } catch (followUpErr) {
            console.error("Follow-up complete failed:", followUpErr);
            toast.warning("Notes saved but failed to mark follow-up complete.");
          }
        } else {
          toast.success("Notes updated successfully!");
        }
      } else {
        toast.success("Notes updated successfully!");
      }
    } catch (err) {
      console.error("Notes update failed:", err);
      toast.error("Failed to update notes");
    } finally {
      setEditingNotes(false);
      setMarkFollowUpComplete(false);
    }
  };

  const handleAddTabNote = async () => {
    if (!lead) return;
    const body = tabNoteDraft.trim();
    if (!body) return;
    setSavingTabNote(true);
    try {
      const res = await API.post(`/leads/${lead.id}/tab-notes/`, { body });
      setTabNotes((prev) => [...prev, res.data]);
      setTabNoteDraft("");
    } catch (err) {
      console.error("Failed to add note:", err);
      toast.error("Failed to add note");
    } finally {
      setSavingTabNote(false);
    }
  };

  const handleAddressEdit = () => {
    setEditingAddress(true);
    setAddressDraft({
      address: lead.address || "",
      city: lead.city || "",
      state: lead.state || "",
      zip_code: lead.zip_code || "",
    });
  };

  const handleAddressCancel = () => {
    setEditingAddress(false);
    setAddressDraft({
      address: lead.address || "",
      city: lead.city || "",
      state: lead.state || "",
      zip_code: lead.zip_code || "",
    });
  };

  const handleAddressSave = async () => {
    if (!lead) return;
    const nextAddress = {
      address: addressDraft.address.trim(),
      city: addressDraft.city.trim(),
      state: normalizeStateValue(addressDraft.state),
      zip_code: addressDraft.zip_code.trim(),
    };
    if (!nextAddress.address || !nextAddress.city || !nextAddress.state) {
      toast.warning("Street, city, and state are required.");
      return;
    }
    const unchanged =
      nextAddress.address === (lead.address || "").trim() &&
      nextAddress.city === (lead.city || "").trim() &&
      nextAddress.state === normalizeStateValue(lead.state) &&
      nextAddress.zip_code === (lead.zip_code || "").trim();
    if (unchanged) {
      setEditingAddress(false);
      return;
    }

    try {
      setSavingAddress(true);
      const res = await API.patch(`/leads/${lead.id}/`, nextAddress);
      setLead(res.data);
      emitLeadUpdate(res.data);
      setEditingAddress(false);
      toast.success("Address updated successfully!");
    } catch (err) {
      console.error("Address update failed:", err);
      toast.error(err.response?.data?.detail || "Failed to update address");
    } finally {
      setSavingAddress(false);
    }
  };

  const handleAdditionalNotesEdit = () => {
    setEditingAdditionalNotes(true);
    setAdditionalNotesText(lead.additional_notes || "");
  };

  const handleAdditionalNotesCancel = () => {
    setEditingAdditionalNotes(false);
    setAdditionalNotesText(lead.additional_notes || "");
  };

  const handleAdditionalNotesSave = async () => {
    if (!canEditAdditionalNotes) return;
    if (!lead) return;

    try {
      const payload = {
        additional_notes: additionalNotesText || "",
      };

      const res = await API.patch(`/leads/${lead.id}/`, payload);
      setLead(res.data);
      emitLeadUpdate(res.data);
      setEditingAdditionalNotes(false);
      toast.success("Additional notes updated successfully!");
    } catch (err) {
      console.error("Additional notes update failed:", err);
      toast.error("Failed to update additional notes");
    }
  };

  const handleDelete = async () => {
    try {
      await API.delete(`/leads/${lead.id}/`);
      toast.success("Lead archived successfully");
      goBack();
    } catch (err) {
      console.error("Delete error:", err);
      toast.error(err.response?.data?.detail || "Failed to archive lead");
    } finally {
      setConfirmDelete(false);
    }
  };

  const handleViewOnMap = () => {
    if (lead?.latitude && lead?.longitude) {
      navigate(`/leads?leadId=${lead.id}&lat=${lead.latitude}&lng=${lead.longitude}&zoom=18`);
    } else {
      toast.warning("This lead doesn't have location coordinates");
    }
  };

  const handleRestore = async () => {
    if (!lead?.id) return;
    
    setRestoringLead(true);
    try {
      await API.post(`/leads/${lead.id}/restore/`);
      toast.success("Lead restored successfully!");
      // Refresh the lead data
      await fetchLead();
      setConfirmRestore(false);
    } catch (err) {
      console.error("Restore error:", err);
      const errorMsg = err.response?.data?.detail || "Failed to restore lead";
      toast.error(errorMsg);
    } finally {
      setRestoringLead(false);
    }
  };

  const renderHeaderStatusChip = (status) => {
    const color = getLeadStatusColor(status);
    return (
      <Chip
        label={status || "New"}
        size="small"
        sx={{
          height: 26,
          borderRadius: "999px",
          fontWeight: 700,
          fontSize: "0.75rem",
          letterSpacing: "-0.01em",
          color: "#343434",
          background: alpha(color, 0.12),
          border: `1px solid ${alpha(color, 0.22)}`,
          "& .MuiChip-label": {
            px: 1.1,
            py: 0,
          },
        }}
      />
    );
  };

  const isClosedWonLead = (lead?.status || "").toLowerCase() === "closed won";
  const isProposalSentLead = (lead?.status || "").toLowerCase() === "proposal sent";
  const canManualFillQuickPush = isClosedWonLead || isProposalSentLead;

  const handlePushToFillQuick = async () => {
    if (!canManualFillQuickPush) {
      toast.info("Only Proposal Sent or Closed Won leads can be pushed to FillQuick.");
      return;
    }
    if (!fillQuickConfigured) {
      toast.info("FillQuick is not configured in Settings.");
      return;
    }
    try {
      const res = await API.post(`/leads/${id}/push-fillquick/`);
      setLead(res.data);
      toast.success("Lead pushed to FillQuick.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Failed to push lead to FillQuick.";
      toast.error(msg);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (!lead) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            Lead not found
          </Typography>
          <Button onClick={goBack} sx={{ mt: 2 }}>
            Back to {fromLabel}
          </Button>
        </Box>
      </Box>
    );
  }

  const fullAddress = [
    lead.address,
    lead.city,
    formatStateAbbr(lead.state),
    lead.zip_code,
  ]
    .filter(Boolean)
    .join(", ");

  const leadBreadcrumbTitle =
    [lead.first_name, lead.last_name].filter(Boolean).join(" ").trim() ||
    lead.company_name ||
    lead.company ||
    (lead.id ? `Lead #${lead.id}` : "Lead");

  const detailCardSx = {
    borderRadius: 3,
    bgcolor: "background.paper",
    border: "1px solid",
    borderColor: (theme) =>
      theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.07)",
    boxShadow: (theme) =>
      theme.palette.mode === "dark"
        ? "0 10px 28px rgba(0,0,0,0.28)"
        : "0 8px 24px rgba(15,23,42,0.055)",
    overflow: "hidden",
  };

  const cardContentSx = {
    p: { xs: 2, md: 2.25 },
    "&:last-child": { pb: { xs: 2, md: 2.25 } },
  };

  const cardHeaderSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: 32,
    mb: 1.25,
    "& > .MuiTypography-root": {
      fontSize: "0.98rem",
      fontWeight: 700,
      letterSpacing: "-0.02em",
      lineHeight: 1.3,
    },
  };

  const compactFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      minHeight: 40,
      fontSize: "0.875rem",
    },
    "& .MuiInputBase-input": { py: 1 },
  };

  const displayRowSx = {
    display: "grid",
    gridTemplateColumns: "22px minmax(105px, 0.9fr) minmax(0, 1.15fr)",
    gap: 1,
    alignItems: "center",
    minHeight: 34,
  };

  const quietIconSx = { fontSize: 18, color: "text.secondary" };



  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Box sx={{ maxWidth: 1400, mx: "auto", width: "100%" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Lead Header - Visual V2 */}
          <Box
            sx={{
              px: { xs: 0.25, md: 0 },
              pt: { xs: 0.25, md: 0 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                mb: 1.35,
                minWidth: 0,
              }}
            >
              {isMobile && (
                <IconButton
                  aria-label={fromLabel.toLowerCase() === "map" ? "Back to map" : `Back to ${fromLabel}`}
                  onClick={goBack}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "14px",
                    flexShrink: 0,
                    color: "#0874f9",
                    bgcolor: (t) => (t.palette.mode === "dark" ? "rgba(8,116,249,0.16)" : "#eef6ff"),
                    "&:hover": {
                      bgcolor: (t) => (t.palette.mode === "dark" ? "rgba(8,116,249,0.24)" : "#e3f0ff"),
                    },
                  }}
                >
                  <ArrowBackIcon size={22} />
                </IconButton>
              )}
              <Breadcrumbs
                aria-label="Lead detail navigation"
                separator="›"
                sx={{
                  fontSize: "0.8125rem",
                  color: "text.secondary",
                  minWidth: 0,
                  "& .MuiBreadcrumbs-separator": { mx: 0.75, color: "text.disabled" },
                  "& .MuiBreadcrumbs-ol": { flexWrap: "nowrap" },
                }}
              >
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  underline="hover"
                  color="text.secondary"
                  onClick={goBack}
                  sx={{
                    cursor: "pointer",
                    fontWeight: 500,
                    border: 0,
                    background: "none",
                    p: 0,
                    fontSize: "0.8125rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  {isMobile
                    ? (fromLabel.toLowerCase() === "map" ? "Back to map" : `Back to ${fromLabel}`)
                    : fromLabel}
                </Link>
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{ fontWeight: 600, fontSize: "0.8125rem" }}
                >
                  Lead
                </Typography>
              </Breadcrumbs>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "flex-start", md: "center" },
                justifyContent: "space-between",
                gap: 2,
                flexDirection: { xs: "column", md: "row" },
              }}
            >
              <Stack spacing={0.55} sx={{ minWidth: 0, flex: 1 }}>
                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  <Typography
                    component="h1"
                    sx={{
                      fontSize: { xs: "1.4rem", sm: "1.65rem", md: "1.85rem" },
                      lineHeight: 1.15,
                      fontWeight: 800,
                      letterSpacing: "-0.03em",
                      color: "text.primary",
                    }}
                  >
                    {(() => {
                      const streetLine = (lead.address || "").trim();
                      const cityLine = [lead.city, formatStateAbbr(lead.state), lead.zip_code]
                        .filter(Boolean)
                        .join(", ");
                      if (!streetLine && !cityLine) return "Lead";
                      return (
                        <>
                          {streetLine && (
                            <Box component="span" sx={{ display: { xs: "block", md: "inline" } }}>
                              {streetLine}
                            </Box>
                          )}
                          {streetLine && cityLine && (
                            <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
                              {", "}
                            </Box>
                          )}
                          {cityLine && (
                            <Box component="span" sx={{ display: { xs: "block", md: "inline" } }}>
                              {cityLine}
                            </Box>
                          )}
                        </>
                      );
                    })()}
                  </Typography>

                  {renderHeaderStatusChip(lead.status)}

                  {lead.is_archived && (
                    <Chip
                      icon={<ArchiveIcon />}
                      label="Archived"
                      size="small"
                      color="warning"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Stack>
                {(() => {
                  const headerName = [lead.first_name, lead.last_name].filter(Boolean).join(" ");
                  const headerMeta = [headerName, formatPhoneDisplay(lead.phone), lead.email].filter(Boolean);
                  if (!headerMeta.length) return null;
                  return (
                    <Typography
                      sx={{
                        fontSize: "0.92rem",
                        fontWeight: 500,
                        color: "text.secondary",
                        lineHeight: 1.4,
                      }}
                    >
                      {headerMeta.join(" · ")}
                    </Typography>
                  );
                })()}
              </Stack>

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  flexShrink: 0,
                  width: { xs: "100%", md: "auto" },
                  flexWrap: { xs: "wrap", md: "nowrap" },
                }}
              >
                {!lead.is_archived && (
                  <>
                    {lead.fillquick_lead_id ? (
                      <Tooltip
                        title={
                          lead.fillquick_pushed_at
                            ? `Pushed at ${new Date(lead.fillquick_pushed_at).toLocaleString()}`
                            : "Pushed to FillQuick"
                        }
                      >
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="In FillQuick"
                          color="success"
                          sx={{
                            height: 40,
                            borderRadius: 2,
                            fontWeight: 600,
                            fontSize: "0.8rem",
                          }}
                        />
                      </Tooltip>
                    ) : canManualFillQuickPush ? (
                      <Tooltip title={fillQuickConfigured ? "Create lead in FillQuick" : "Configure FillQuick in Settings first"}>
                        <span>
                          <Button
                            variant="outlined"
                            startIcon={<SendIcon />}
                            onClick={handlePushToFillQuick}
                            disabled={!fillQuickConfigured}
                            sx={{
                              ...headerOutlineButtonSx,
                              minHeight: 40,
                              borderRadius: 2,
                              boxShadow: "none",
                            }}
                          >
                            Push to FillQuick
                          </Button>
                        </span>
                      </Tooltip>
                    ) : null}

                    {canDeleteLead && (
                      <>
                        <IconButton
                          aria-label="More lead actions"
                          onClick={(event) => setHeaderMenuAnchor(event.currentTarget)}
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            color: "text.primary",
                            bgcolor: "background.paper",
                            "&:hover": { bgcolor: "action.hover" },
                          }}
                        >
                          <MoreHorizontalIcon size={20} />
                        </IconButton>
                        <Menu
                          anchorEl={headerMenuAnchor}
                          open={Boolean(headerMenuAnchor)}
                          onClose={() => setHeaderMenuAnchor(null)}
                          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                          transformOrigin={{ vertical: "top", horizontal: "right" }}
                          slotProps={{
                            paper: {
                              sx: {
                                mt: 0.75,
                                minWidth: 170,
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                boxShadow: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "0 12px 28px rgba(0,0,0,0.35)"
                                    : "0 12px 28px rgba(15,23,42,0.12)",
                              },
                            },
                          }}
                        >
                          <MenuItem
                            onClick={() => {
                              setHeaderMenuAnchor(null);
                              setConfirmDelete(true);
                            }}
                            sx={{
                              gap: 1,
                              mx: 0.5,
                              borderRadius: 1.5,
                              fontSize: "0.8125rem",
                              color: "error.main",
                            }}
                          >
                            <DeleteIcon size={17} />
                            Delete Lead
                          </MenuItem>
                        </Menu>
                      </>
                    )}
                  </>
                )}

                {lead.is_archived && (role === "Admin" || role === "Sales Director") && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<RestoreIcon />}
                    onClick={() => setConfirmRestore(true)}
                    disabled={isEditDisabled || restoringLead}
                    sx={{
                      minHeight: 40,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    {restoringLead ? "Restoring..." : "Restore Lead"}
                  </Button>
                )}
              </Stack>
            </Box>
          </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, minmax(0, 1fr))",
              md: "repeat(7, max-content)",
            },
            justifyContent: { md: "start" },
            columnGap: { xs: 1, md: 0.25 },
            rowGap: { xs: 1, md: 0 },
            borderBottomWidth: { xs: 0, md: 1 },
            borderBottomStyle: "solid",
            borderBottomColor: "divider",
            mt: { xs: 1.5, md: -0.5 },
            mb: { xs: 2.25, md: 0 },
          }}
        >
          {[
            ["overview", "Overview", LayoutDashboardIcon],
            ["proposals", "Proposals", null],
            ["notes", "Notes", StickyNoteIcon],
            ["followups", `Follow-ups (${followUps.length})`, ClockIcon],
            ["files", `Files (${leadFiles.length})`, FolderIcon],
            ["adt", "ADT", ShieldCheckIcon],
            ["activity", "Activity", ActivityIcon],
          ].map(([id, label, TabIcon]) => {
            const selected = leadTab === id;

            return (
              <Button
                key={id}
                disableRipple
                onClick={() => setLeadTab(id)}
                startIcon={
                  TabIcon ? <TabIcon size={19} strokeWidth={1.9} /> : null
                }
                sx={{
                  position: "relative",
                  minHeight: { xs: 54, md: 40 },
                  px: { xs: 1.75, md: 1.35 },
                  py: { xs: 1.25, md: 1 },
                  borderRadius: { xs: "11px", md: 0 },
                  justifyContent: { xs: "flex-start", md: "center" },
                  gridColumn: {
                    xs: id === "activity" ? "1 / -1" : "auto",
                    md: "auto",
                  },
                  border: { xs: "1px solid", md: "none" },
                  borderColor: (theme) => ({
                    xs: selected
                      ? "rgba(0,99,242,0.16)"
                      : theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.10)"
                        : "rgba(15,23,42,0.08)",
                    md: "transparent",
                  }),
                  bgcolor: (theme) => ({
                    xs: selected
                      ? "rgba(0,99,242,0.07)"
                      : theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.04)"
                        : "rgba(255,255,255,0.72)",
                    md: "transparent",
                  }),
                  color: selected ? "primary.main" : "text.secondary",
                  fontWeight: selected ? 600 : 500,
                  fontSize: { xs: "0.875rem", md: "0.92rem" },
                  letterSpacing: 0,
                  textTransform: "none",
                  boxShadow: (theme) => ({
                    xs: theme.palette.mode === "dark"
                      ? "none"
                      : selected
                        ? "0 1px 2px rgba(15,23,42,0.03)"
                        : "0 1px 2px rgba(15,23,42,0.025)",
                    md: "none",
                  }),
                  "& .MuiButton-startIcon": {
                    mr: { xs: 1.1, md: 0.75 },
                    ml: 0,
                    color:
                      id === "followups" && followUps.some((followUp) => !followUp.completed)
                        ? "#F57C00"
                        : selected
                          ? "primary.main"
                          : "text.secondary",
                    "& svg": {
                      width: { xs: 19, md: 18 },
                      height: { xs: 19, md: 18 },
                    },
                  },
                  "&:hover": {
                    bgcolor: (theme) => ({
                      xs: selected
                        ? "rgba(0,99,242,0.09)"
                        : theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.06)"
                          : "rgba(15,23,42,0.035)",
                      md: "transparent",
                    }),
                    color: "primary.main",
                  },
                  "&::after": {
                    content: '""',
                    display: { xs: "none", md: "block" },
                    position: "absolute",
                    left: 11,
                    right: 11,
                    bottom: 0,
                    height: "2px",
                    bgcolor: selected ? "primary.main" : "transparent",
                  },
                }}
              >
                {id === "proposals" ? (
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <ProposalTabStatusIcon status={leadProposals[0]?.status || "draft"} />
                    {label}
                  </Box>
                ) : (
                  label
                )}
              </Button>
            );
          })}
        </Box>

        {/* ------------------------------------------------------------------Main Grid - Visual V2 */}
        <Box
          sx={{
            display: leadTab === "overview" || leadTab === "notes" ? "grid" : "none",
            gridTemplateColumns: { xs: "1fr", md: "0.94fr 1.08fr 1fr" },
            gap: 2,
            alignItems: "stretch",
          }}
        >
          {/* LEFT COLUMN */}
          {leadTab === "overview" && (
          <Stack spacing={2} sx={{ minWidth: 0 }}>
            <Card data-tour="lead-detail-contact-info" sx={{ ...detailCardSx, scrollMarginTop: "16px" }}>
              <CardContent sx={cardContentSx}>
                <Box sx={cardHeaderSx}>
                  <Typography fontWeight={700}>Contact Information</Typography>
                  {!editingContact && (
                    <Button
                      data-tour="lead-detail-contact-edit"
                      type="button"
                      size="small"
                      variant="outlined"
                      onClick={handleContactEdit}
                      disabled={isEditDisabled || !canEditLead}
                      aria-label="Edit"
                      startIcon={!isMobile ? <EditIcon size={15} /> : null}
                      sx={{
                        minWidth: isMobile ? 34 : "auto",
                        width: isMobile ? 34 : "auto",
                        height: 34,
                        px: isMobile ? 0 : 1.15,
                        borderRadius: 2,
                        textTransform: "none",
                        fontSize: "0.78rem",
                        fontWeight: 600,
                        lineHeight: 1,
                        color: "primary.main",
                        borderColor: (theme) => alpha(theme.palette.primary.main, 0.22),
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.025),
                        boxShadow: "none",
                        pointerEvents: "auto",
                        position: "relative",
                        zIndex: 2,
                        "& .MuiButton-startIcon": { mr: 0.65 },
                        "&:hover": {
                          borderColor: (theme) => alpha(theme.palette.primary.main, 0.42),
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.07),
                          boxShadow: "none",
                        },
                      }}
                    >
                      {isMobile ? <EditIcon size={16} /> : "Edit"}
                    </Button>
                  )}
                </Box>
                <Divider sx={{ mb: 1.5 }} />

                {editingContact ? (
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Name</Typography>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 0.5 }}>
                        <TextField value={contactData.first_name}
                          onChange={(e) => setContactData({ ...contactData, first_name: e.target.value })}
                          size="small" fullWidth placeholder="First name" disabled={isEditDisabled} sx={compactFieldSx} />
                        <TextField value={contactData.last_name}
                          onChange={(e) => setContactData({ ...contactData, last_name: e.target.value })}
                          size="small" fullWidth placeholder="Last name" disabled={isEditDisabled} sx={compactFieldSx} />
                      </Stack>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Email</Typography>
                      <TextField type="email" value={contactData.email}
                        onChange={(e) => setContactData({ ...contactData, email: e.target.value })}
                        size="small" fullWidth disabled={isEditDisabled} sx={{ ...compactFieldSx, mt: 0.5 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Phone</Typography>
                      <TextField type="tel" value={formatPhone(contactData.phone)}
                        onChange={(e) => setContactData({ ...contactData, phone: formatPhone(e.target.value) })}
                        placeholder="(xxx) xxx-xxxx"
                        size="small" fullWidth disabled={isEditDisabled} sx={{ ...compactFieldSx, mt: 0.5 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">SMS/Email Opt-In</Typography>
                      <FormControl fullWidth size="small" disabled={isEditDisabled || !contactData.phone} sx={{ mt: 0.5 }}>
                        <Select value={contactData.sms_email_opt_in ?? ""}
                          onChange={(e) => setContactData({ ...contactData, sms_email_opt_in: e.target.value })}
                          displayEmpty sx={{ borderRadius: 2, minHeight: 40, fontSize: "0.875rem" }}>
                          <MenuItem value=""><em>—</em></MenuItem>
                          <MenuItem value="yes">Yes</MenuItem>
                          <MenuItem value="no">No</MenuItem>
                        </Select>
                      </FormControl>
                      {!contactData.phone && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                          Enter a phone number to set opt-in status
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Language</Typography>
                      <FormControl fullWidth size="small" disabled={isEditDisabled} sx={{ mt: 0.5 }}>
                        <Select
                          value={contactData.proposal_language === "es" ? "es" : "en"}
                          onChange={(e) => setContactData({ ...contactData, proposal_language: e.target.value })}
                          sx={{ borderRadius: 2, minHeight: 40, fontSize: "0.875rem" }}
                        >
                          <MenuItem value="en">English</MenuItem>
                          <MenuItem value="es">Spanish</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ pt: 0.5 }}>
                      <Button size="small" variant="text" onClick={handleContactCancel}
                        sx={{ textTransform: "none", fontWeight: 600 }}>Cancel</Button>
                      <Button size="small" variant="contained" onClick={handleContactSave}
                        disabled={isEditDisabled || !canEditLead}
                        sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, px: 2 }}>Save changes</Button>
                    </Stack>
                  </Stack>
                ) : (
                  <Stack spacing={0.85}>
                    <Box sx={displayRowSx}>
                      <PersonIcon sx={{ ...quietIconSx, color: "#2563eb" }} />
                      <Typography variant="body2" color="text.secondary">Name</Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {lead.first_name || lead.last_name ? [lead.first_name, lead.last_name].filter(Boolean).join(" ") : "—"}
                      </Typography>
                    </Box>
                    <Box sx={displayRowSx}>
                      <PhoneIcon sx={{ ...quietIconSx, color: "#16a34a" }} />
                      <Typography variant="body2" color="text.secondary">Phone</Typography>
                      <Typography variant="body2" fontWeight={500}>{formatPhoneDisplay(lead.phone) || "—"}</Typography>
                    </Box>
                    <Box sx={displayRowSx}>
                      <EmailIcon sx={{ ...quietIconSx, color: "#2563eb" }} />
                      <Typography variant="body2" color="text.secondary">Email</Typography>
                      <Typography variant="body2" fontWeight={500} noWrap>{lead.email || "—"}</Typography>
                    </Box>
                    <Box sx={displayRowSx}>
                      <MessageSquareIcon sx={{ ...quietIconSx, color: "#2563eb" }} />
                      <Typography variant="body2" color="text.secondary">SMS/Email Opt-In</Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {lead.sms_email_opt_in === "yes" ? "Yes" : lead.sms_email_opt_in === "no" ? "No" : "Not on File"}
                      </Typography>
                    </Box>
                    <Box sx={displayRowSx}>
                      <GlobeIcon sx={{ ...quietIconSx, color: "#2563eb" }} />
                      <Typography variant="body2" color="text.secondary">Language</Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {lead.proposal_language === "es" ? "Spanish" : "English"}
                      </Typography>
                    </Box>
                  </Stack>
                )}
              </CardContent>
            </Card>

            <Card sx={detailCardSx}>
              <CardContent
                sx={{
                  ...cardContentSx,
                  pb: { xs: 2, md: 4.5 },
                  "&:last-child": { pb: { xs: 2, md: 4.5 } },
                }}
              >
                <Box sx={cardHeaderSx}>
                  <Typography fontWeight={700}>Lead Details</Typography>
                  {!editingLeadDetails && (
                    <Button
                      type="button"
                      size="small"
                      variant="outlined"
                      onClick={handleLeadDetailsEdit}
                      disabled={isEditDisabled || !canEditLead}
                      aria-label="Edit"
                      startIcon={!isMobile ? <EditIcon size={15} /> : null}
                      sx={{
                        minWidth: isMobile ? 34 : "auto",
                        width: isMobile ? 34 : "auto",
                        height: 34,
                        px: isMobile ? 0 : 1.15,
                        borderRadius: 2,
                        textTransform: "none",
                        fontSize: "0.78rem",
                        fontWeight: 600,
                        lineHeight: 1,
                        color: "primary.main",
                        borderColor: (theme) => alpha(theme.palette.primary.main, 0.22),
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.025),
                        boxShadow: "none",
                        pointerEvents: "auto",
                        position: "relative",
                        zIndex: 2,
                        "& .MuiButton-startIcon": { mr: 0.65 },
                        "&:hover": {
                          borderColor: (theme) => alpha(theme.palette.primary.main, 0.42),
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.07),
                          boxShadow: "none",
                        },
                      }}
                    >
                      {isMobile ? <EditIcon size={16} /> : "Edit"}
                    </Button>
                  )}
                </Box>
                <Divider sx={{ mb: 1.5 }} />

                {editingLeadDetails ? (
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Status</Typography>
                      <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                        <Select value={contactData.status || ""}
                          onChange={(e) => {
                            const nextStatus = e.target.value;
                            setContactData({ ...contactData, status: nextStatus });
                            if (nextStatus !== "Closed Won") setAssignedRepError(false);
                          }}
                          disabled={isEditDisabled}
                          renderValue={(selected) => (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <LeadStatusIcon status={selected} size={18} />
                              <Typography variant="body2" fontWeight={600}>{selected || "—"}</Typography>
                            </Stack>
                          )}
                          sx={{ borderRadius: 2, minHeight: 40 }}>
                          {STATUS_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <LeadStatusIcon status={option.value} size={18} />
                                <Typography variant="body2">{option.label || option.value}</Typography>
                              </Stack>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {contactData.status === "Follow Up Needed" && (
                      <TextField id="lead-detail-follow-up-date" label="Follow-Up Date" type="date" size="small" fullWidth
                        value={contactData.followUpDate}
                        onChange={(e) => setContactData({ ...contactData, followUpDate: e.target.value })}
                        InputLabelProps={{ shrink: true }} required disabled={isEditDisabled}
                        helperText={followUps?.find((f) => !f.completed) ? "Updating existing follow-up date" : "Creating new follow-up"}
                        sx={compactFieldSx} />
                    )}

                    {canManageLeadCategory && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">Lead Source</Typography>
                        <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                          <Select value={contactData.category || ""}
                            onChange={(e) => setContactData({ ...contactData, category: e.target.value })}
                            displayEmpty disabled={isEditDisabled}
                            sx={{ borderRadius: 2, minHeight: 40, fontSize: "0.875rem" }}>
                            <MenuItem value=""><em>No lead source</em></MenuItem>
                            {leadCategories.map((cat) => (
                              <MenuItem key={cat.id} value={cat.id}>{displayLeadCategoryLabel(cat.label)}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    )}

                    <Box>
                      <Typography variant="caption" color="text.secondary">Assigned Rep</Typography>
                      <FormControl
                        fullWidth
                        size="small"
                        error={assignedRepError}
                        disabled={isEditDisabled || role === "Sales Rep"}
                        sx={{ mt: 0.5 }}
                      >
                        <Select value={contactData.assigned_rep || ""}
                          onChange={(e) => {
                            setContactData({ ...contactData, assigned_rep: e.target.value });
                            if (e.target.value) setAssignedRepError(false);
                          }}
                          displayEmpty sx={{ borderRadius: 2, minHeight: 40, fontSize: "0.875rem" }}>
                          <MenuItem value=""><em>Unassigned</em></MenuItem>
                          {buildAssignedRepOptions({
                            users,
                            currentRepId: contactData.assigned_rep,
                            currentRepName: lead.assigned_rep_name,
                          }).map((repOption) => (
                            <MenuItem key={repOption.id} value={repOption.id}>{formatAssignedRepOptionLabel(repOption)}</MenuItem>
                          ))}
                        </Select>
                        {assignedRepError && (
                          <FormHelperText>Lead must have an assigned rep</FormHelperText>
                        )}
                      </FormControl>
                    </Box>

                    <Divider />
                    <Stack spacing={1}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">Territory</Typography>
                        <Typography variant="body2" fontWeight={500}>{lead.territory_name || "—"}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">Created</Typography>
                        <Typography variant="body2" fontWeight={500} textAlign="right">
                          {lead.created_at ? new Date(lead.created_at).toLocaleString() : "—"}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" justifyContent="flex-end" spacing={1}>
                      <Button size="small" variant="text" onClick={handleLeadDetailsCancel}
                        sx={{ textTransform: "none", fontWeight: 600 }}>Cancel</Button>
                      <Button size="small" variant="contained" onClick={handleLeadDetailsSave}
                        disabled={isEditDisabled || !canEditLead}
                        sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, px: 2 }}>Save changes</Button>
                    </Stack>
                  </Stack>
                ) : (
                  <Stack spacing={0.85}>
                    <Box sx={displayRowSx}>
                      <LeadStatusIcon status={lead.status} size={18} />
                      <Typography variant="body2" color="text.secondary">Status</Typography>
                      <Box>{renderHeaderStatusChip(lead.status)}</Box>
                    </Box>
                    {canManageLeadCategory && (
                      <Box sx={displayRowSx}>
                        <TagIcon sx={{ ...quietIconSx, color: "#2563eb" }} />
                        <Typography variant="body2" color="text.secondary">Lead Source</Typography>
                        <Box><Chip size="small" variant="outlined"
                          label={displayLeadCategoryLabel(lead.category_label)}
                          sx={leadCategoryChipSx(getLeadCategoryOutlineColor(lead))} /></Box>
                      </Box>
                    )}
                    <Box sx={displayRowSx}>
                      <PersonIcon sx={{ ...quietIconSx, color: "#7c3aed" }} />
                      <Typography variant="body2" color="text.secondary">Assigned Rep</Typography>
                      <Typography variant="body2" fontWeight={500}>{lead.assigned_rep_name || "Unassigned"}</Typography>
                    </Box>
                    <Box sx={displayRowSx}>
                      <LocationOnIcon sx={{ ...quietIconSx, color: "#2563eb" }} />
                      <Typography variant="body2" color="text.secondary">Territory</Typography>
                      <Typography variant="body2" fontWeight={500}>{lead.territory_name || "—"}</Typography>
                    </Box>
                    {lead.source && (
                      <Box sx={displayRowSx}>
                        <GlobeIcon sx={{ ...quietIconSx, color: "#0891b2" }} />
                        <Typography variant="body2" color="text.secondary">Source</Typography>
                        <Typography variant="body2" fontWeight={500}>{lead.source}</Typography>
                      </Box>
                    )}
                    <Box sx={displayRowSx}>
                      <CalendarTodayIcon sx={{ ...quietIconSx, color: "#2563eb" }} />
                      <Typography variant="body2" color="text.secondary">Created</Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {lead.created_at ? new Date(lead.created_at).toLocaleString() : "—"}
                      </Typography>
                    </Box>
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Stack>
          )}

          {/* CENTER COLUMN */}
          {leadTab === "overview" && (
          <Stack spacing={2} sx={{ minWidth: 0 }}>
            <Card data-tour="lead-detail-property-details" sx={{ ...detailCardSx, height: "100%" }}>
              <CardContent sx={cardContentSx}>
                <Box sx={cardHeaderSx}>
                  <Typography fontWeight={700}>Property Details</Typography>
                  {editingAddress ? (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                      <Button
                        size="small"
                        variant="text"
                        onClick={handleAddressCancel}
                        disabled={savingAddress}
                        sx={{ textTransform: "none", fontWeight: 600, minHeight: 34 }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleAddressSave}
                        disabled={isEditDisabled || !canEditLead || savingAddress}
                        sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, minHeight: 34 }}
                      >
                        {savingAddress ? "Saving..." : "Save changes"}
                      </Button>
                    </Stack>
                  ) : (
                    <Button
                      type="button"
                      size="small"
                      variant="outlined"
                      onClick={handleAddressEdit}
                      disabled={isEditDisabled || !canEditLead}
                      aria-label="Edit"
                      startIcon={!isMobile ? <EditIcon size={15} /> : null}
                      sx={{
                        minWidth: isMobile ? 34 : "auto",
                        width: isMobile ? 34 : "auto",
                        height: 34,
                        px: isMobile ? 0 : 1.15,
                        borderRadius: 2,
                        textTransform: "none",
                        fontSize: "0.78rem",
                        fontWeight: 600,
                        lineHeight: 1,
                        color: "primary.main",
                        borderColor: (theme) => alpha(theme.palette.primary.main, 0.22),
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.025),
                        boxShadow: "none",
                        pointerEvents: "auto",
                        position: "relative",
                        zIndex: 2,
                        "& .MuiButton-startIcon": { mr: 0.65 },
                        "&:hover": {
                          borderColor: (theme) => alpha(theme.palette.primary.main, 0.42),
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.07),
                          boxShadow: "none",
                        },
                      }}
                    >
                      {isMobile ? <EditIcon size={16} /> : "Edit"}
                    </Button>
                  )}
                </Box>
                <Divider sx={{ mb: 1.5 }} />
                <Box sx={{ display: "flex", alignItems: editingAddress ? "flex-start" : "center", justifyContent: "space-between", gap: 1.5,
                  pb: 1.5, mb: 1.1, borderBottom: "1px solid", borderColor: "divider",
                  flexWrap: { xs: "wrap", sm: "nowrap" } }}>
                  <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
                    <HomeIcon
                      size={24}
                      sx={{ color: "#2563eb", flexShrink: 0, mt: 0.25 }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      {editingAddress ? (
                        <Stack spacing={1}>
                          <TextField
                            value={addressDraft.address}
                            onChange={(e) => setAddressDraft((prev) => ({ ...prev, address: e.target.value }))}
                            size="small"
                            fullWidth
                            autoFocus
                            placeholder="Street address"
                            disabled={isEditDisabled || savingAddress}
                            sx={compactFieldSx}
                          />
                          <TextField
                            value={addressDraft.city}
                            onChange={(e) => setAddressDraft((prev) => ({ ...prev, city: e.target.value }))}
                            size="small"
                            fullWidth
                            placeholder="City"
                            disabled={isEditDisabled || savingAddress}
                            sx={compactFieldSx}
                          />
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <StateSelect
                                size="small"
                                placeholder="State"
                                value={addressDraft.state}
                                onChange={(e) => setAddressDraft((prev) => ({ ...prev, state: e.target.value }))}
                                disabled={isEditDisabled || savingAddress}
                              />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <TextField
                                value={addressDraft.zip_code}
                                onChange={(e) => setAddressDraft((prev) => ({ ...prev, zip_code: e.target.value }))}
                                size="small"
                                fullWidth
                                placeholder="ZIP"
                                disabled={isEditDisabled || savingAddress}
                                sx={compactFieldSx}
                              />
                            </Box>
                          </Stack>
                        </Stack>
                      ) : (
                        <>
                          <Typography variant="body2" fontWeight={700} noWrap>{lead.address || "Address not available"}</Typography>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {[lead.city, formatStateAbbr(lead.state), lead.zip_code].filter(Boolean).join(", ") || "—"}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Stack>
                  <Button data-tour="lead-detail-view-on-map" variant="outlined" startIcon={<MapIcon />}
                    onClick={handleViewOnMap} disabled={!lead.latitude || !lead.longitude}
                    sx={{ ...headerOutlineButtonSx, minHeight: 40, flexShrink: 0, boxShadow: "none", borderRadius: 2 }}>
                    View on Map
                  </Button>
                </Box>
                <Stack spacing={0.55}>
                  <Box sx={displayRowSx}><CalendarTodayIcon sx={{ ...quietIconSx, color: "#2563eb" }} />
                    <Typography variant="body2" color="text.secondary">Sold Date</Typography>
                    <Typography variant="body2" fontWeight={600}>{lead.sold_date ? new Date(lead.sold_date).toLocaleDateString() : "—"}</Typography></Box>
                  <Box sx={displayRowSx}><DollarSignIcon sx={{ ...quietIconSx, color: "#16a34a" }} />
                    <Typography variant="body2" color="text.secondary">Price</Typography>
                    <Typography variant="body2" fontWeight={600}>{lead.price ? `$${parseFloat(lead.price).toLocaleString()}` : "—"}</Typography></Box>
                  <Box sx={displayRowSx}><RulerIcon sx={{ ...quietIconSx, color: "#7c3aed" }} />
                    <Typography variant="body2" color="text.secondary">Square Feet</Typography>
                    <Typography variant="body2" fontWeight={600}>{lead.square_feet ? `${parseFloat(lead.square_feet).toLocaleString()} sq ft` : "—"}</Typography></Box>
                  <Box sx={displayRowSx}><HomeIcon sx={{ ...quietIconSx, color: "#2563eb" }} />
                    <Typography variant="body2" color="text.secondary">Year Built</Typography>
                    <Typography variant="body2" fontWeight={600}>{lead.year_built || "—"}</Typography></Box>
                  <Box sx={displayRowSx}><TagIcon sx={{ ...quietIconSx, color: "#7c3aed" }} />
                    <Typography variant="body2" color="text.secondary">Property Type</Typography>
                    <Typography variant="body2" fontWeight={600}>{lead.property_type || "—"}</Typography></Box>
                </Stack>

                {lead.latitude && lead.longitude && (
                  <Box
                    sx={{
                      mt: 1.35,
                      pt: 1.35,
                      borderTop: "1px solid",
                      borderColor: "divider",
                      display: "grid",
                      gridTemplateColumns: "22px minmax(0, 1fr)",
                      gap: 1,
                      alignItems: "start",
                    }}
                  >
                    <LocationOnIcon
                      size={18}
                      sx={{ color: "#2563eb", mt: 0.15 }}
                    />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Location Coordinates
                      </Typography>
                      <Typography
                        sx={{
                          mt: 0.25,
                          fontFamily: "monospace",
                          fontSize: "0.72rem",
                          color: "text.primary",
                        }}
                      >
                        Lat: {lead.latitude} &nbsp;&nbsp; Lng: {lead.longitude}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Stack>
          )}

          {leadTab === "overview" && (
          <Stack
            spacing={1.5}
            sx={{
              minWidth: 0,
              gridColumn: leadTab === "notes" ? { md: "1 / span 2" } : undefined,
              height: leadTab === "overview" ? "100%" : { xs: "auto", md: 560 },
              minHeight: leadTab === "notes" ? { xs: 460, md: 560 } : undefined,
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: { md: 0 },
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
            <Card data-tour="lead-detail-notes-followups" sx={detailCardSx}>
              <CardContent sx={cardContentSx}>
                <Box sx={cardHeaderSx}>
                  <Typography fontWeight={700}>Notes</Typography>
                  {!editingNotes && (
                    <Button
                      type="button"
                      size="small"
                      variant="outlined"
                      onClick={handleNotesEdit}
                      disabled={isEditDisabled || !canEditLead}
                      aria-label="Edit"
                      startIcon={!isMobile ? <EditIcon size={15} /> : null}
                      sx={{
                        minWidth: isMobile ? 34 : "auto",
                        width: isMobile ? 34 : "auto",
                        height: 34,
                        px: isMobile ? 0 : 1.15,
                        borderRadius: 2,
                        textTransform: "none",
                        fontSize: "0.78rem",
                        fontWeight: 600,
                        lineHeight: 1,
                        color: "primary.main",
                        borderColor: (theme) => alpha(theme.palette.primary.main, 0.22),
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.025),
                        boxShadow: "none",
                        pointerEvents: "auto",
                        position: "relative",
                        zIndex: 2,
                        "& .MuiButton-startIcon": { mr: 0.65 },
                        "&:hover": {
                          borderColor: (theme) => alpha(theme.palette.primary.main, 0.42),
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.07),
                          boxShadow: "none",
                        },
                      }}
                    >
                      {isMobile ? <EditIcon size={16} /> : "Edit"}
                    </Button>
                  )}
                </Box>
                <Divider sx={{ mb: 1.5 }} />
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>NOTES</Typography>
                  {editingNotes ? (
                    <>
                      <TextField multiline rows={3} value={notesText}
                        onChange={(e) => setNotesText(e.target.value)}
                        size="small" fullWidth placeholder="Add notes..." disabled={isEditDisabled}
                        sx={{ ...compactFieldSx, mt: 0.75 }} />
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 1 }}>
                        <Button size="small" variant="contained" onClick={handleNotesSave}
                          disabled={isEditDisabled || !canEditLead}
                          sx={{ textTransform: "none", borderRadius: 2 }}>Save</Button>
                        <Button size="small" variant="text" onClick={handleNotesCancel} sx={{ textTransform: "none" }}>Cancel</Button>
                        {followUps?.find((f) => !f.completed) && (
                          <FormControlLabel control={<Checkbox checked={markFollowUpComplete}
                            onChange={(e) => setMarkFollowUpComplete(e.target.checked)} size="small" />}
                            label="Mark follow-up completed"
                            sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.75rem" } }} />
                        )}
                      </Stack>
                    </>
                  ) : (
                    <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: "pre-wrap" }}>
                      {lead.notes || "No notes available."}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
            {canViewAdditionalNotes && (
              <Card sx={detailCardSx}>
                <CardContent sx={cardContentSx}>
                  <Box sx={cardHeaderSx}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <DescriptionIcon sx={{ color: "primary.main", fontSize: 19 }} />
                      <Typography fontWeight={700}>Additional Notes</Typography>
                    </Stack>
                    {canEditAdditionalNotes && !editingAdditionalNotes && (
                      <Button
                        type="button"
                        size="small"
                        variant="outlined"
                        onClick={handleAdditionalNotesEdit}
                        disabled={isEditDisabled || !canEditLead}
                        aria-label="Edit"
                        startIcon={!isMobile ? <EditIcon size={15} /> : null}
                        sx={{
                          minWidth: isMobile ? 34 : "auto",
                          width: isMobile ? 34 : "auto",
                          height: 34,
                          px: isMobile ? 0 : 1.15,
                          borderRadius: 2,
                          textTransform: "none",
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          lineHeight: 1,
                          color: "primary.main",
                          borderColor: (theme) => alpha(theme.palette.primary.main, 0.22),
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.025),
                          boxShadow: "none",
                          pointerEvents: "auto",
                          position: "relative",
                          zIndex: 2,
                          "& .MuiButton-startIcon": { mr: 0.65 },
                          "&:hover": {
                            borderColor: (theme) => alpha(theme.palette.primary.main, 0.42),
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.07),
                            boxShadow: "none",
                          },
                        }}
                      >
                        {isMobile ? <EditIcon size={16} /> : "Edit"}
                      </Button>
                    )}
                  </Box>
                  <Divider sx={{ mb: 1.5 }} />
                  {editingAdditionalNotes ? (
                    <>
                      <TextField multiline rows={4} value={additionalNotesText}
                        onChange={(e) => setAdditionalNotesText(e.target.value)}
                        size="small" fullWidth placeholder="Add additional notes..."
                        disabled={isEditDisabled} sx={compactFieldSx} />
                      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 1.25 }}>
                        <Button size="small" variant="text" onClick={handleAdditionalNotesCancel} sx={{ textTransform: "none" }}>Cancel</Button>
                        <Button size="small" variant="contained" onClick={handleAdditionalNotesSave}
                          disabled={isEditDisabled || !canEditLead}
                          sx={{ textTransform: "none", borderRadius: 2 }}>Save changes</Button>
                      </Stack>
                    </>
                  ) : (
                    <Box sx={{ p: 1.5, borderRadius: 2,
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.035),
                      border: "1px solid", borderColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                      minHeight: 92 }}>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                        {lead.additional_notes || "No additional notes."}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
            </Box>
          </Stack>
          )}

          {leadTab === "notes" && (
          <Stack
            spacing={1.5}
            sx={{
              minWidth: 0,
              gridColumn: { md: "1 / span 2" },
              height: { xs: "auto", md: 560 },
              minHeight: { xs: 460, md: 560 },
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: { md: 0 },
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <Card data-tour="lead-detail-notes-followups" sx={{ ...detailCardSx, flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                <CardContent sx={{ ...cardContentSx, flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                  <Box sx={cardHeaderSx}>
                    <Typography fontWeight={700}>Notes</Typography>
                  </Box>
                  <Divider sx={{ mb: 1.5 }} />
                  <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                    {tabNotes.map((note) => (
                      <Box
                        key={note.id}
                        sx={{
                          mb: 1.25,
                          borderBottom: "1px solid",
                          borderColor: "divider",
                          pb: 1.25,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: 1,
                            flexWrap: "wrap",
                            px: 1,
                            py: 0.6,
                            mb: 0.75,
                            bgcolor: (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.06)"
                                : "rgba(15,23,42,0.04)",
                          }}
                        >
                          <Typography variant="body2" fontWeight={700}>
                            {note.author}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatLeadTabNoteStamp(note.created_at)}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", px: 1, lineHeight: 1.55 }}>
                          {note.body}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  <TextField
                    multiline
                    minRows={4}
                    value={tabNoteDraft}
                    onChange={(e) => setTabNoteDraft(e.target.value)}
                    size="small"
                    fullWidth
                    placeholder="Your note"
                    disabled={isEditDisabled || !canEditLead || savingTabNote}
                    sx={{ ...compactFieldSx, mt: 1 }}
                  />
                  <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.25 }}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleAddTabNote}
                      disabled={isEditDisabled || !canEditLead || savingTabNote || !tabNoteDraft.trim()}
                      sx={{ textTransform: "none", borderRadius: 2 }}
                    >
                      Add note
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          </Stack>
          )}
        </Box>

        {leadTab === "followups" && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "0.94fr 1.08fr 1fr" },
              gap: 2,
            }}
          >
            <Card
              sx={{
                ...detailCardSx,
                gridColumn: { md: "1 / span 2" },
                minHeight: { xs: 460, md: 560 },
                height: { md: 560 },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <CardContent sx={{ ...cardContentSx, flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                <Typography fontWeight={700} sx={{ mb: 1.5 }}>Follow-ups</Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                  {followUps.length > 0 ? (
                    <Stack spacing={1}>
                      {followUps.map((followUp) => (
                        <Box key={followUp.id} sx={{ p: 1.25, borderRadius: 2, border: "1px solid",
                          borderColor: "divider", bgcolor: followUp.completed ? "action.hover" : "background.paper" }}>
                          <Stack direction="row" justifyContent="space-between" spacing={1}>
                            <Box sx={{ minWidth: 0 }}>
                              {followUp.title && (
                                <Typography variant="body2" fontWeight={600}>
                                  {followUp.title.replace(/^Follow Up: Follow Up for /, "Follow-up for ")}
                                </Typography>
                              )}
                              {followUp.due_date && (
                                <Typography variant="caption" color="text.secondary">
                                  Due: {new Date(followUp.due_date).toLocaleDateString()}
                                </Typography>
                              )}
                              {followUp.notes && <Typography variant="caption" sx={{ display: "block", mt: 0.35 }}>{followUp.notes}</Typography>}
                            </Box>
                            <Chip label={followUp.completed ? "Done" : "Pending"} size="small"
                              color={followUp.completed ? "success" : "warning"} />
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No follow-ups for this lead.
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        {leadTab === "activity" && token && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "0.94fr 1.08fr 1fr" },
              gap: 2,
            }}
          >
            <Card
              data-tour="lead-detail-activity-timeline"
              sx={{
                ...detailCardSx,
                gridColumn: { md: "1 / span 2" },
                height: { xs: 460, md: 560 },
                minHeight: { xs: 460, md: 560 },
                maxHeight: { xs: 460, md: 560 },
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <CardContent sx={{ ...cardContentSx, flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
                <Box sx={cardHeaderSx}><Typography fontWeight={700}>Activity Timeline</Typography></Box>
                <Divider sx={{ mb: 1.5 }} />
                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                  <ActivityTimeline token={token} leadId={lead.id} />
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        {leadTab === "files" && (
          <Card sx={detailCardSx}>
            <CardContent sx={cardContentSx}>
              <Typography fontWeight={700}>Files</Typography>
              <Divider sx={{ my: 1.5 }} />
              {leadFiles.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No files are attached to this lead.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {leadFiles.map((file) => (
                    <Box
                      key={file.id}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 2,
                        alignItems: "center",
                        py: 0.75,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {file.url ? (
                          <Box
                            component="a"
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ color: "primary.main", textDecoration: "none" }}
                          >
                            {file.label}
                          </Box>
                        ) : (
                          file.label
                        )}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        )}

        {leadTab === "adt" && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "0.94fr 1.08fr 1fr" },
              gap: 2,
            }}
          >
          <Card sx={{ ...detailCardSx, gridColumn: { md: "1 / span 2" } }}>
            <CardContent sx={cardContentSx}>
              <Typography fontWeight={700} color="error.main">ADT</Typography>
              <Divider sx={{ my: 1.5 }} />
              <Typography fontWeight={700} sx={{ mb: 0.75 }}>Address</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Address not yet pre-qualified.
              </Typography>
              <Button variant="outlined" disabled sx={{ textTransform: "none", mb: 2.5 }}>
                Pre-Qualify Address
              </Button>
              <Typography fontWeight={700} sx={{ mb: 0.75 }}>Credit</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Credit check not yet performed.
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" disabled sx={{ textTransform: "none" }}>Run Credit</Button>
                <Button variant="outlined" disabled sx={{ textTransform: "none" }}>Enter Credit Info</Button>
              </Stack>
            </CardContent>
          </Card>
          </Box>
        )}

        {leadTab === "proposals" && !lead.is_archived && activeProposalId && proposalStatus !== "draft" && (
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={beginNewProposal}
              disabled={isEditDisabled}
              sx={{ textTransform: "none", fontWeight: 700, minHeight: 40, borderRadius: 2 }}
            >
              Create new proposal
            </Button>
          </Box>
        )}

        {/* ----------------------------------------------Proposal History */}
        {leadTab === "proposals" && proposalHistory.length > 0 && (
          <Card
            sx={{
              borderRadius: 3,
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(30,41,59,0.88)"
                  : "background.paper",
              border: "1px solid",
              borderColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.06)",
              boxShadow: (theme) =>
                theme.palette.mode === "dark"
                  ? "0 8px 24px rgba(0,0,0,0.55)"
                  : "0 4px 12px rgba(0,0,0,0.12)",
              mt: 0,
              mb: 2,
            }}
          >
            <CardContent>
              <Typography fontWeight={700} sx={{ mb: 1 }}>
                Saved proposals ({proposalHistory.length})
              </Typography>
              <Divider sx={{ mb: 1 }} />
              {isMobile ? renderMobileRows() : renderDesktopRows()}
            </CardContent>
          </Card>
        )}



        {/* -------------------------------------------------------------------Proposal Builder V4 */}
        {leadTab === "proposals" && !lead.is_archived && (
          <Card sx={{ ...detailCardSx, mt: 0 }}>
            <CardContent sx={{ p: { xs: 2, md: 2.5 }, "&:last-child": { pb: { xs: 2, md: 2.5 } } }}>
              <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, gap: 1.5, mb: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: "calc(1.12rem + 1pt)", fontWeight: 700, color: "text.primary" }}>
                    {proposalStatus === "draft"
                      ? "Create Proposal"
                      : proposalStatus === "sent"
                      ? "Sent Proposal"
                      : proposalStatus === "accepted"
                      ? "Accepted Proposal"
                      : proposalStatus === "declined"
                      ? "Declined Proposal"
                      : "Withdrawn Proposal"}
                  </Typography>
                  <Typography sx={{ fontSize: "calc(0.8rem + 1pt)", color: "text.secondary", mt: 0.3 }}>
                    {[lead.first_name, lead.last_name].filter(Boolean).join(" ") || "Lead"}{fullAddress ? ` · ${fullAddress}` : ""}
                  </Typography>
                </Box>
                {proposalStatus !== "draft" && (
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<VisibilityOutlinedIcon fontSize="small" />}
                      sx={proposalHistoryViewButtonSx}
                      onClick={() => openQuickView({ id: activeProposalId, proposalId: activeProposalId })}
                    >
                      View
                    </Button>
                    {proposalStatus === "sent" && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        disabled={isEditDisabled}
                        startIcon={<CancelIcon fontSize="small" />}
                        sx={{ textTransform: "none", fontSize: "calc(0.75rem + 1pt)", minHeight: 32 }}
                        onClick={() => {
                          setWithdrawProposalId(activeProposalId);
                          setConfirmWithdrawOpen(true);
                        }}
                      >
                        Withdraw
                      </Button>
                    )}
                    {(proposalStatus === "sent" || proposalStatus === "withdrawn") && (
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={isEditDisabled}
                        startIcon={<EditIcon fontSize="small" />}
                        sx={{ textTransform: "none", fontSize: "calc(0.75rem + 1pt)", minHeight: 32 }}
                        onClick={() => reopenProposalForEdit(activeProposalId)}
                      >
                        Edit
                      </Button>
                    )}
                  </Stack>
                )}
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr auto 1fr auto 1fr" }, gap: 1, alignItems: "center", mb: 2.5 }}>
                {[
                  [1, "Package & Equipment", "Choose and customize"],
                  [2, "Monitoring", "Select monthly service"],
                  [3, "Review", "Discount & confirmation"],
                ].map(([n, title, sub], idx) => (
                  <React.Fragment key={n}>
                    <Button
                      disabled={proposalStatus === "draft" && n === 3 && !selectedMonthlyCharge}
                      onClick={() => {
                        if (proposalStatus === "draft" && n === 3 && !selectedMonthlyCharge) return;
                        setProposalBuilderStep(n);
                      }}
                      sx={{
                        justifyContent: "flex-start",
                        textTransform: "none",
                        p: 1.15,
                        borderRadius: 2.25,
                        color: proposalBuilderStep === n ? "#075fd8" : proposalBuilderStep > n ? "#168a54" : "text.primary",
                        bgcolor: proposalBuilderStep === n
                          ? (theme) => theme.palette.mode === "dark" ? alpha("#1683f3", 0.16) : "#edf5ff"
                          : "transparent",
                        "&:hover": { bgcolor: proposalBuilderStep === n ? "#edf5ff" : (theme) => alpha(theme.palette.primary.main, 0.04) },
                      }}
                    >
                      <Box sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        mr: 1.1,
                        flexShrink: 0,
                        bgcolor: proposalBuilderStep === n ? "#1683f3" : proposalBuilderStep > n ? "#22a06b" : "action.hover",
                        color: proposalBuilderStep >= n ? "#fff" : "text.secondary",
                        fontWeight: 700,
                        boxShadow: proposalBuilderStep === n ? "0 0 0 5px rgba(22,131,243,0.10)" : "none",
                      }}>
                        {proposalBuilderStep > n ? <Box component="span" sx={{ fontSize: "calc(1rem + 1pt)", fontWeight: 700, lineHeight: 1 }}>✓</Box> : n}
                      </Box>
                      <Box sx={{ textAlign: "left" }}>
                        <Typography sx={{ fontSize: "calc(0.84rem + 1pt)", fontWeight: 700, lineHeight: 1.2 }}>{title}</Typography>
                        <Typography sx={{ fontSize: "calc(0.72rem + 1pt)", color: "text.secondary", mt: 0.2 }}>{sub}</Typography>
                      </Box>
                    </Button>
                    {idx < 2 && <Typography sx={{ display: { xs: "none", sm: "block" }, color: "text.disabled" }}>›</Typography>}
                  </React.Fragment>
                ))}
              </Box>

              {proposalBuilderStep === 1 && (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "0.9fr 1.2fr 0.9fr" }, gap: 1.5 }}>
                  <Box sx={{
                    border: "1px solid",
                    borderColor: { xs: (theme) => alpha(theme.palette.primary.main, 0.24), lg: "divider" },
                    borderRadius: 2.5,
                    p: 1.5,
                    bgcolor: { xs: (theme) => theme.palette.mode === "dark" ? alpha(theme.palette.primary.main, 0.08) : alpha(theme.palette.primary.main, 0.045), lg: "transparent" },
                    boxShadow: { xs: (theme) => `inset 3px 0 0 ${alpha(theme.palette.primary.main, 0.9)}`, lg: "none" },
                  }}>
                    <Typography sx={{ fontSize: "calc(0.9rem + 1pt)", fontWeight: 700 }}>Build Equipment</Typography>
                    <Typography sx={{ fontSize: "calc(0.72rem + 1pt)", color: "text.secondary", mb: 1.25 }}>Start with a kit or build item by item.</Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", bgcolor: "action.hover", p: 0.4, borderRadius: 2, mb: 1.5 }}>
                      <Button onClick={() => setProposalBuildMode("kits")} sx={{ textTransform: "none", fontSize: "calc(0.72rem + 1pt)", fontWeight: 700, borderRadius: 1.5, bgcolor: proposalBuildMode === "kits" ? "background.paper" : "transparent" }}>Packages / Kits</Button>
                      <Button onClick={() => setProposalBuildMode("individual")} sx={{ textTransform: "none", fontSize: "calc(0.72rem + 1pt)", fontWeight: 700, borderRadius: 1.5, bgcolor: proposalBuildMode === "individual" ? "background.paper" : "transparent" }}>Individual Products</Button>
                    </Box>
                    {proposalBuildMode === "kits" ? (
                      <Stack spacing={1}>
                        {packagesCatalog.map((pkg) => (
                          <Box key={pkg.id} sx={{ p: 1.25, border: "1px solid", borderColor: selectedProposalPackageName === pkg.name ? "primary.main" : "divider", borderRadius: 2, "&:hover": { borderColor: proposalFieldsLocked ? "divider" : "primary.main" } }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                              <Typography sx={{ fontSize: "calc(0.76rem + 1pt)", fontWeight: 700 }}>{pkg.name}</Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={proposalFieldsLocked || selectedProposalPackageName === pkg.name}
                                onClick={() => addPackageToProposal(pkg)}
                                sx={{
                                  minWidth: 62,
                                  textTransform: "none",
                                  fontSize: "calc(0.68rem + 1pt)",
                                  fontWeight: 600,
                                  flexShrink: 0,
                                  ...(selectedProposalPackageName === pkg.name
                                    ? {
                                        color: "#168a54",
                                        borderColor: "#22a06b",
                                        "&:hover": { borderColor: "#168a54", bgcolor: (theme) => alpha("#22a06b", theme.palette.mode === "dark" ? 0.10 : 0.06) },
                                      }
                                    : {}),
                                }}
                              >
                                {selectedProposalPackageName === pkg.name ? "✓ Added" : "+ Add"}
                              </Button>
                            </Stack>
                            <Typography sx={{ fontSize: "calc(0.68rem + 1pt)", color: "text.secondary", mt: 0.5 }}>
                              {pkg.description?.trim() || "No description provided."}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Stack spacing={1}>
                        <FormControl size="small" fullWidth><InputLabel>Category</InputLabel><Select value={proposalCategoryFilter} label="Category" onChange={(e) => setProposalCategoryFilter(e.target.value)} disabled={proposalFieldsLocked} sx={proposalSelectSx} MenuProps={proposalMenuProps}><MenuItem value=""><em>All Categories</em></MenuItem>{proposalCategories.filter((c) => normalizeCategoryKey(c.label) !== proposalPackagesCategoryKey).map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}</Select></FormControl>
                        <Box
                          sx={{
                            maxHeight: { xs: 292, lg: "none" },
                            overflowY: { xs: "auto", lg: "visible" },
                            pr: { xs: 0.4, lg: 0 },
                            scrollbarWidth: "thin",
                            "&::-webkit-scrollbar": { width: 5 },
                            "&::-webkit-scrollbar-thumb": {
                              borderRadius: 999,
                              bgcolor: (theme) => alpha(theme.palette.text.secondary, 0.28),
                            },
                          }}
                        >
                          <Stack spacing={1}>
                            {proposalProducts.map((product) => (
                              <Box key={product.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, p: 1, border: "1px solid", borderColor: "divider", borderRadius: 2, bgcolor: { xs: "background.paper", lg: "transparent" } }}>
                                <Box sx={{ minWidth: 0 }}><Typography sx={{ fontSize: "calc(0.73rem + 1pt)", fontWeight: 700 }}>{product.name}</Typography><Typography sx={{ fontSize: "calc(0.66rem + 1pt)", color: "text.secondary" }}>{product.sku || "—"} · ${parseFloat(product.price || 0).toFixed(2)}</Typography></Box>
                                <Button size="small" variant="outlined" disabled={proposalFieldsLocked} onClick={() => addProductToProposal(product)} sx={{ minWidth: 54, textTransform: "none", fontSize: "calc(0.68rem + 1pt)" }}>+ Add</Button>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                        {proposalProducts.length > 5 && (
                          <Typography sx={{ display: { xs: "block", lg: "none" }, textAlign: "center", fontSize: "calc(0.68rem + 1pt)", color: "text.secondary", pt: 0.25 }}>
                            Scroll for more products ↓
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </Box>

                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ display: { xs: "flex", lg: "none" }, px: 0.5, py: 0.15 }}
                  >
                    <Divider sx={{ flex: 1 }} />
                    <Box sx={{ width: 26, height: 26, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: "primary.main", color: "primary.contrastText", fontSize: "calc(0.85rem + 1pt)", fontWeight: 700 }}>↓</Box>
                    <Typography sx={{ fontSize: "calc(0.68rem + 1pt)", fontWeight: 700, color: "primary.main", whiteSpace: "nowrap" }}>Added to proposal</Typography>
                    <Divider sx={{ flex: 1 }} />
                  </Stack>

                  <Box sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2.5,
                    p: 1.5,
                    bgcolor: { xs: (theme) => theme.palette.mode === "dark" ? alpha(theme.palette.text.primary, 0.035) : "#f8fafc", lg: "transparent" },
                  }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1} mb={1}>
                      <Box><Typography sx={{ fontSize: "calc(0.9rem + 1pt)", fontWeight: 700 }}>Proposal Equipment</Typography><Typography sx={{ fontSize: "calc(0.72rem + 1pt)", color: "text.secondary" }}>Working proposal line-item list.</Typography></Box>
                      <Button size="small" variant="outlined" onClick={() => setProposalBuildMode("individual")} sx={{ display: { xs: "none", lg: "inline-flex" }, textTransform: "none", fontSize: "calc(0.68rem + 1pt)" }}>+ Add Product</Button>
                    </Stack>
                    {proposalItems.length === 0 ? <Box sx={{ minHeight: 260, border: "1px dashed", borderColor: "divider", borderRadius: 2, display: "grid", placeItems: "center", color: "text.secondary", fontSize: "calc(0.75rem + 1pt)" }}>Select a package or add individual products.</Box> : <Stack divider={<Divider flexItem />}>
                      {proposalItems.map((item) => <Box key={item.id} sx={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto auto", gap: 1, alignItems: "center", py: 1 }}>
                        <Box sx={{ minWidth: 0 }}><Typography sx={{ fontSize: "calc(0.73rem + 1pt)", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.product_name || "Unknown Product"}</Typography><Typography sx={{ fontSize: "calc(0.65rem + 1pt)", color: "text.secondary" }}>{item.product?.sku || "—"} · ${parseFloat(item.unit_price || 0).toFixed(2)} each</Typography></Box>
                        <Stack direction="row" alignItems="center" spacing={0.5}><IconButton size="small" disabled={proposalFieldsLocked || item.quantity <= 1} onClick={() => updateProposalItemQuantity(item.id, Math.max(1, (item.quantity || 1) - 1))} sx={{ width: 28, height: 28, bgcolor: (theme) => theme.palette.mode === "dark" ? alpha("#1683f3", 0.14) : "#eef6ff", color: "#0874f9", "&:hover": { bgcolor: (theme) => alpha("#1683f3", 0.16) } }}>−</IconButton><Typography sx={{ minWidth: 20, textAlign: "center", fontSize: "calc(0.75rem + 1pt)", fontWeight: 700 }}>{item.quantity || 1}</Typography><IconButton size="small" disabled={proposalFieldsLocked} onClick={() => updateProposalItemQuantity(item.id, (item.quantity || 1) + 1)} sx={{ width: 28, height: 28, bgcolor: (theme) => theme.palette.mode === "dark" ? alpha("#1683f3", 0.14) : "#eef6ff", color: "#0874f9", "&:hover": { bgcolor: (theme) => alpha("#1683f3", 0.16) } }}>+</IconButton></Stack>
                        <IconButton size="small" color="error" disabled={proposalFieldsLocked} onClick={() => removeProposalItem(item.id)}><DeleteIcon fontSize="small" /></IconButton>
                      </Box>)}
                    </Stack>}
                  </Box>

                  <Box sx={{
                    border: "1px solid",
                    borderColor: { xs: (theme) => theme.palette.mode === "dark" ? alpha("#22a06b", 0.28) : alpha("#22a06b", 0.24), lg: "divider" },
                    borderRadius: 2.5,
                    p: 1.5,
                    bgcolor: { xs: (theme) => theme.palette.mode === "dark" ? alpha("#22a06b", 0.07) : alpha("#22a06b", 0.045), lg: "transparent" },
                  }}>
                    <Typography sx={{ display: { xs: "block", lg: "none" }, fontSize: "calc(0.62rem + 1pt)", fontWeight: 700, color: "#168a54", letterSpacing: "0.08em", textTransform: "uppercase", mb: 0.35 }}>Summary</Typography>
                    <Typography sx={{ fontSize: "calc(0.9rem + 1pt)", fontWeight: 700, mb: 1 }}>Selection Summary</Typography>
                    <Typography sx={{ fontSize: "calc(0.7rem + 1pt)", color: "text.secondary", mb: 1 }}>Current Equipment</Typography>
                    <Stack spacing={0.75}>{proposalItems.map((item) => <Stack key={item.id} direction="row" justifyContent="space-between" gap={1}><Typography sx={{ fontSize: "calc(0.68rem + 1pt)", color: "text.secondary" }}>{item.product_name} × {item.quantity || 1}</Typography><Typography sx={{ fontSize: "calc(0.68rem + 1pt)", fontWeight: 700 }}>${(parseFloat(item.unit_price || 0) * (item.quantity || 1)).toFixed(2)}</Typography></Stack>)}</Stack>
                    <Divider sx={{ my: 1.25 }} /><Stack direction="row" justifyContent="space-between"><Typography sx={{ fontSize: "calc(0.76rem + 1pt)", fontWeight: 700 }}>Equipment Subtotal</Typography><Typography sx={{ fontSize: "calc(0.8rem + 1pt)", fontWeight: 700 }}>${proposalSubtotal.toFixed(2)}</Typography></Stack>
                  </Box>
                </Box>
              )}

              {proposalBuilderStep === 2 && (
                <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2.5, p: { xs: 1.5, md: 2 } }}>
                  <Typography sx={{ fontSize: "calc(0.7rem + 1pt)", fontWeight: 700, color: "primary.main", mb: 0.5 }}>MONTHLY SERVICE CHARGES</Typography><Typography sx={{ fontSize: "calc(1rem + 1pt)", fontWeight: 700 }}>Choose Monitoring</Typography><Typography sx={{ fontSize: "calc(0.74rem + 1pt)", color: "text.secondary", mb: 1.5 }}>{selectedKit?.assigned_monthly_charge && visibleMonthlyCharges.length === 1 ? "This package includes one monitoring plan. The rate starts at the top of its range and can be lowered in $1 steps." : "Select the standard service, then adjust this proposal's monthly price in $1 increments if needed."}</Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0,1fr))" }, gap: 1.25 }}>
                    {visibleMonthlyCharges.map((p) => {
                      const bounds = monthlyPriceBounds(p);
                      const cardPrice = bounds ? bounds.max : Number(p.price || 0);
                      return <Box key={p.id} onClick={() => !proposalFieldsLocked && handleMonthlySelect({ target: { value: p.id } })} sx={{ p: 1.5, border: "1px solid", borderColor: String(selectedMonthlyCharge?.id) === String(p.id) ? "primary.main" : "divider", borderRadius: 2.25, cursor: proposalFieldsLocked ? "default" : "pointer", bgcolor: String(selectedMonthlyCharge?.id) === String(p.id) ? (theme) => theme.palette.mode === "dark" ? alpha("#1683f3", 0.14) : "#f1f7ff" : "transparent", boxShadow: String(selectedMonthlyCharge?.id) === String(p.id) ? "0 6px 18px rgba(8,116,249,0.08)" : "none" }}><Typography sx={{ fontSize: "calc(0.78rem + 1pt)", fontWeight: 700 }}>{p.name}</Typography>{p.description?.trim() && <Typography sx={{ fontSize: "calc(0.68rem + 1pt)", color: "text.secondary", mt: 0.45, lineHeight: 1.4 }}>{p.description}</Typography>}<Typography sx={{ fontSize: "calc(1rem + 1pt)", fontWeight: 700, color: "primary.main", mt: 0.75 }}>${cardPrice.toFixed(2)} <Box component="span" sx={{ fontSize: "calc(0.68rem + 1pt)", color: "text.secondary" }}>/ mo</Box></Typography>{bounds && <Typography sx={{ fontSize: "calc(0.66rem + 1pt)", color: "text.secondary", mt: 0.35 }}>${bounds.min.toFixed(2)} – ${bounds.max.toFixed(2)}</Typography>}</Box>;
                    })}
                  </Box>
                  {!selectedMonthlyCharge && (
                    <Typography sx={{ fontSize: "calc(0.72rem + 1pt)", color: "text.secondary", mt: 1.25 }}>
                      Select a monitoring package to continue.
                    </Typography>
                  )}
                  {selectedMonthlyCharge && <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: "action.hover", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}><Box><Typography sx={{ fontSize: "calc(0.68rem + 1pt)", color: "text.secondary" }}>Selected monthly service</Typography><Typography sx={{ fontSize: "calc(0.78rem + 1pt)", fontWeight: 700 }}>{selectedMonthlyCharge.name}</Typography><Typography sx={{ fontSize: "calc(0.66rem + 1pt)", color: "text.secondary" }}>{selectedMonthlyBounds ? `Range $${selectedMonthlyBounds.min.toFixed(2)} – $${selectedMonthlyBounds.max.toFixed(2)}` : `Standard price: $${Number(selectedMonthlyCharge.price || 0).toFixed(2)} / month`}</Typography></Box><Stack direction="row" alignItems="center" spacing={1}><IconButton disabled={proposalFieldsLocked || monthlyAtMin} onClick={() => adjustMonthlyServiceCharge(-1)} sx={{ bgcolor: (theme) => theme.palette.mode === "dark" ? alpha("#1683f3", 0.14) : "#eef6ff", color: "#0874f9" }}>−</IconButton><Typography sx={{ minWidth: 105, textAlign: "center", fontSize: "calc(1.1rem + 1pt)", fontWeight: 700, color: "primary.main" }}>${Number(monthlyServiceCharge || 0).toFixed(2)}<Box component="span" sx={{ fontSize: "calc(0.68rem + 1pt)", color: "text.secondary", ml: 0.5 }}>/mo</Box></Typography><IconButton disabled={proposalFieldsLocked || monthlyAtMax} onClick={() => adjustMonthlyServiceCharge(1)} sx={{ bgcolor: (theme) => theme.palette.mode === "dark" ? alpha("#1683f3", 0.14) : "#eef6ff", color: "#0874f9" }}>+</IconButton></Stack></Box>}
                  {selectedMonthlyCharge && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography sx={{ fontSize: "calc(0.72rem + 1pt)", fontWeight: 600, mb: 0.55 }}>Add On</Typography>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={monitoringAddonName}
                          onChange={(e) => {
                            const name = e.target.value;
                            if (!name) {
                              setMonitoringAddonName("");
                              setMonitoringAddonCharge(null);
                              return;
                            }
                            const option = monitoringAddonOptions.find((item) => item.name === name);
                            setMonitoringAddonName(name);
                            setMonitoringAddonCharge(option ? option.price : null);
                          }}
                          displayEmpty
                          disabled={proposalFieldsLocked}
                          sx={{ ...proposalSelectSx, minHeight: 40, height: 40, "& .MuiSelect-select": { fontSize: "calc(0.84rem + 1pt)", fontWeight: 500, py: 0.9 } }}
                          MenuProps={proposalMenuProps}
                        >
                          <MenuItem value=""><em>None</em></MenuItem>
                          {[
                            ...monitoringAddonOptions,
                            ...(monitoringAddonName && !monitoringAddonOptions.some((item) => item.name === monitoringAddonName)
                              ? [{ name: monitoringAddonName, price: Number(monitoringAddonCharge) || 0 }]
                              : []),
                          ].map((option) => (
                            <MenuItem key={option.name} value={option.name}>
                              {option.name} · ${Number(option.price).toFixed(2)}/mo
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                </Box>
              )}

              {proposalBuilderStep === 3 && (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
                  <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2.5, p: 1.5 }}>
                    <Typography sx={{ fontSize: "calc(0.95rem + 1pt)", fontWeight: 700, mb: 1 }}>Review Proposal</Typography>
                    <Stack spacing={1}>
                      <Box sx={{ p: 1.35, bgcolor: (theme) => theme.palette.mode === "dark" ? alpha("#1683f3", 0.10) : "#f5f9fe", border: "1px solid", borderColor: (theme) => theme.palette.mode === "dark" ? alpha("#fff", 0.08) : "#e7eef7", borderRadius: 2 }}>
                        <Typography sx={{ fontSize: "calc(0.76rem + 1pt)", fontWeight: 700, mb: 0.75 }}>
                          {selectedProposalPackageName || "Custom Equipment"}
                        </Typography>
                        <Typography sx={{ fontSize: "calc(0.7rem + 1pt)", color: "text.secondary", mb: 0.75 }}>
                          Current Equipment
                        </Typography>
                        <Stack spacing={0.75}>
                          {proposalItems.map((item) => (
                            <Stack key={item.id} direction="row" justifyContent="space-between" alignItems="baseline" gap={1.5}>
                              <Typography sx={{ fontSize: "calc(0.68rem + 1pt)", color: "text.secondary", lineHeight: 1.35, minWidth: 0 }}>
                                {item.product_name || item.product?.name || "Unknown Product"} × {item.quantity || 1}
                              </Typography>
                              <Typography sx={{ fontSize: "calc(0.68rem + 1pt)", fontWeight: 700, whiteSpace: "nowrap", color: "text.primary" }}>
                                ${(parseFloat(item.unit_price || 0) * (item.quantity || 1)).toFixed(2)}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                        <Divider sx={{ my: 1.25 }} />
                        <Stack direction="row" justifyContent="space-between" gap={1}>
                          <Typography sx={{ fontSize: "calc(0.76rem + 1pt)", fontWeight: 700 }}>
                            Equipment Subtotal
                          </Typography>
                          <Typography sx={{ fontSize: "calc(0.8rem + 1pt)", fontWeight: 700 }}>${proposalSubtotal.toFixed(2)}</Typography>
                        </Stack>
                      </Box>
                      {Number(proposalDiscount || 0) > 0 && (
                        <Box sx={{ p: 1.35, bgcolor: (theme) => theme.palette.mode === "dark" ? alpha("#1683f3", 0.10) : "#f5f9fe", border: "1px solid", borderColor: (theme) => theme.palette.mode === "dark" ? alpha("#fff", 0.08) : "#e7eef7", borderRadius: 2 }}>
                          <Typography sx={{ fontSize: "calc(0.7rem + 1pt)", color: "text.secondary", fontWeight: 600 }}>Discount</Typography>
                          <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1.5}>
                            <Typography sx={{ fontSize: "calc(0.76rem + 1pt)", fontWeight: 700, minWidth: 0 }}>
                              {selectedDiscount?.name || (proposalDiscountLabel && proposalDiscountLabel !== "Discount" ? proposalDiscountLabel : "Custom")}
                            </Typography>
                            <Typography sx={{ fontSize: "calc(0.76rem + 1pt)", fontWeight: 700, color: "error.main", whiteSpace: "nowrap" }}>
                              −${Number(proposalDiscount || 0).toFixed(2)}
                            </Typography>
                          </Stack>
                        </Box>
                      )}
                      <Box sx={{ p: 1.35, bgcolor: (theme) => theme.palette.mode === "dark" ? alpha("#1683f3", 0.10) : "#f5f9fe", border: "1px solid", borderColor: (theme) => theme.palette.mode === "dark" ? alpha("#fff", 0.08) : "#e7eef7", borderRadius: 2 }}>
                        <Typography sx={{ fontSize: "calc(0.7rem + 1pt)", color: "text.secondary", fontWeight: 600 }}>Monitoring</Typography>
                        <Stack spacing={0.45}>
                          <Stack direction="row" justifyContent="space-between" gap={1}>
                            <Typography sx={{ fontSize: "calc(0.76rem + 1pt)", fontWeight: 700 }}>{proposalMonthlyLineLabel}</Typography>
                            <Typography sx={{ fontSize: "calc(0.76rem + 1pt)", fontWeight: 700 }}>{proposalMonthlyLineAmountText}</Typography>
                          </Stack>
                          {hasMonitoringAddon && (
                            <Stack direction="row" justifyContent="space-between" gap={1}>
                              <Typography sx={{ fontSize: "calc(0.76rem + 1pt)", fontWeight: 700 }}>{monitoringAddonName}</Typography>
                              <Typography sx={{ fontSize: "calc(0.76rem + 1pt)", fontWeight: 700 }}>${Number(monitoringAddonCharge).toFixed(2)}/mo</Typography>
                            </Stack>
                          )}
                          {hasMonitoringAddon && (
                            <Stack direction="row" justifyContent="space-between" gap={1}>
                              <Typography sx={{ fontSize: "calc(0.76rem + 1pt)", fontWeight: 700 }}>Total Monthly Monitoring</Typography>
                              <Typography sx={{ fontSize: "calc(0.76rem + 1pt)", fontWeight: 700 }}>${totalMonthlyMonitoring.toFixed(2)}/mo</Typography>
                            </Stack>
                          )}
                          <Typography sx={{ fontSize: "calc(0.66rem + 1pt)", color: "text.secondary", pt: 0.35 }}>
                            36 Month Agreement
                          </Typography>
                        </Stack>
                      </Box>
                      <Box sx={{ p: 1.35, bgcolor: (theme) => theme.palette.mode === "dark" ? alpha("#1683f3", 0.10) : "#f5f9fe", border: "1px solid", borderColor: (theme) => theme.palette.mode === "dark" ? alpha("#fff", 0.08) : "#e7eef7", borderRadius: 2 }}>
                        <Stack spacing={0.75}>
                          <Stack direction="row" justifyContent="space-between" gap={1}>
                            <Typography sx={{ fontSize: "calc(0.72rem + 1pt)" }}>Equipment subtotal</Typography>
                            <Typography sx={{ fontSize: "calc(0.72rem + 1pt)", fontWeight: 700 }}>${proposalSubtotal.toFixed(2)}</Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between" gap={1}>
                            <Typography sx={{ fontSize: "calc(0.72rem + 1pt)" }}>Discount</Typography>
                            <Typography sx={{ fontSize: "calc(0.72rem + 1pt)", fontWeight: 700, color: "error.main" }}>−${Number(proposalDiscount || 0).toFixed(2)}</Typography>
                          </Stack>
                          {activationFee != null && Number(activationFee) > 0 && (
                            <Stack direction="row" justifyContent="space-between" gap={1}>
                              <Typography sx={{ fontSize: "calc(0.72rem + 1pt)" }}>ADT Activation Fee</Typography>
                              <Typography sx={{ fontSize: "calc(0.72rem + 1pt)", fontWeight: 700 }}>${Number(activationFee).toFixed(2)}</Typography>
                            </Stack>
                          )}
                          <Stack direction="row" justifyContent="space-between" gap={1}>
                            <Typography sx={{ fontSize: "calc(0.8rem + 1pt)", fontWeight: 700 }}>Total (one-time)</Typography>
                            <Typography sx={{ fontSize: "calc(0.85rem + 1pt)", fontWeight: 700 }}>${proposalTotal.toFixed(2)}</Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between" gap={1}>
                            <Typography sx={{ fontSize: "calc(0.72rem + 1pt)" }}>Monthly service</Typography>
                            <Typography sx={{ fontSize: "calc(0.72rem + 1pt)", fontWeight: 700, color: "primary.main" }}>{hasMonitoringAddon ? `$${totalMonthlyMonitoring.toFixed(2)}/mo` : proposalMonthlyLineAmountText}</Typography>
                          </Stack>
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>
                  <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2.5, p: 1.5 }}><Typography sx={{ fontSize: "calc(0.7rem + 1pt)", fontWeight: 700, color: "primary.main" }}>DISCOUNTS</Typography><Typography sx={{ fontSize: "calc(0.95rem + 1pt)", fontWeight: 700, mb: 0.25 }}>Discount & Terms</Typography><Typography sx={{ fontSize: "calc(0.72rem + 1pt)", color: "text.secondary", mb: 1.25 }}>Select the discount type and adjust the amount if needed.</Typography><Stack spacing={1.1}><Box><Typography sx={{ fontSize: "calc(0.72rem + 1pt)", fontWeight: 600, mb: 0.55 }}>Discount Type</Typography><FormControl size="small" fullWidth><Select value={selectedDiscount?.id || ""} onChange={handleDiscountSelect} disabled={proposalFieldsLocked} displayEmpty sx={{ ...proposalSelectSx, minHeight: 40, height: 40, "& .MuiSelect-select": { fontSize: "calc(0.84rem + 1pt)", fontWeight: 500, py: 0.9 } }} MenuProps={proposalMenuProps}><MenuItem value=""><em>Custom</em></MenuItem>{discountProducts.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}</Select></FormControl></Box><Box><Typography sx={{ fontSize: "calc(0.72rem + 1pt)", fontWeight: 600, mb: 0.55 }}>Discount Amount</Typography><TextField size="small" type="number" value={proposalDiscount || ""} onChange={(e) => setProposalDiscount(e.target.value === "" ? 0 : Number(e.target.value))} disabled={proposalFieldsLocked} sx={{ ...proposalTextFieldSx, "& .MuiOutlinedInput-root": { ...proposalControlSx, minHeight: 40, height: 40 }, "& .MuiInputBase-input": { fontSize: "calc(0.84rem + 1pt)", fontWeight: 600, py: 0.9 } }} InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: "text.secondary", fontSize: "calc(0.82rem + 1pt)" }}>$</Typography> }} />{selectedDiscount?.name && <Typography sx={{ mt: 0.45, fontSize: "calc(0.68rem + 1pt)", color: "text.secondary" }}>{selectedDiscount.name}</Typography>}</Box><Box><Typography sx={{ fontSize: "calc(0.72rem + 1pt)", fontWeight: 600, mb: 0.55 }}>ADT Activation Fee</Typography><FormControl size="small" fullWidth><Select value={activationFee != null && Number(activationFee) > 0 ? Number(activationFee).toFixed(2) : ""} onChange={(e) => { const next = e.target.value; setActivationFee(next === "" ? null : Number(next)); }} disabled={proposalFieldsLocked} displayEmpty sx={{ ...proposalSelectSx, minHeight: 40, height: 40, "& .MuiSelect-select": { fontSize: "calc(0.84rem + 1pt)", fontWeight: 500, py: 0.9 } }} MenuProps={proposalMenuProps}><MenuItem value=""><em>None</em></MenuItem>{activationFeeSelectOptions.map((option) => <MenuItem key={option.amount.toFixed(2)} value={option.amount.toFixed(2)}>{option.label}</MenuItem>)}</Select></FormControl></Box><Box><Typography sx={{ fontSize: "calc(0.72rem + 1pt)", fontWeight: 600, mb: 0.55 }}>Special terms and promotions</Typography><TextField multiline minRows={2} maxRows={4} fullWidth size="small" placeholder="List any special arrangements agreed with the customer" value={proposalSpecialTerms} onChange={(e) => setProposalSpecialTerms(e.target.value)} disabled={proposalFieldsLocked} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", alignItems: "flex-start" }, "& textarea": { fontSize: "calc(0.8rem + 1pt)", lineHeight: 1.45, minHeight: "72px" }, "& textarea::placeholder": { color: "text.secondary", opacity: 0.85 } }} /></Box></Stack></Box>
                </Box>
              )}

              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} gap={1.5} sx={{ mt: 2 }}>
                <Box>
                  {proposalBuilderStep > 1 && (
                    <Button variant="outlined" onClick={() => setProposalBuilderStep((s) => Math.max(1, s - 1))} sx={{ textTransform: "none" }}>← Back</Button>
                  )}
                </Box>
                {proposalBuilderStep < 3 ? <Button variant="contained" disabled={proposalStatus === "draft" && ((proposalBuilderStep === 1 && proposalItems.length === 0) || (proposalBuilderStep === 2 && !selectedMonthlyCharge))} onClick={() => { if (proposalStatus === "draft" && proposalBuilderStep === 2 && !selectedMonthlyCharge) return; setProposalBuilderStep((s) => Math.min(3, s + 1)); }} sx={{ textTransform: "none", minWidth: 170, bgcolor: "#0874f9", fontWeight: 700, boxShadow: "0 6px 16px rgba(8,116,249,0.20)", "&:hover": { bgcolor: "#0667df" } }}>Next: {proposalBuilderStep === 1 ? "Monitoring" : "Review"} →</Button> : proposalStatus === "draft" ? <Stack direction={{ xs: "column", sm: "row" }} spacing={1}><Button variant="outlined" disabled={proposalFieldsLocked} onClick={async () => { try { const saved = await saveProposalDraft(); if (!saved) return; await loadLeadProposalsFromNewApi(lead.id); } catch (err) { console.error(err); toast.error(err?.response?.data?.detail || "Failed to save draft proposal."); } }} sx={{ textTransform: "none" }}>Save Draft</Button>{proposalItems.length > 0 && proposalStatus === "draft" && <Button variant="contained" startIcon={<SendIcon />} disabled={proposalFieldsLocked} onClick={async () => { if (proposalFieldsLocked) return; if (!(lead?.email || "").trim()) { setProposalEmailRequiredOpen(true); return; } try { const draftId = await ensureDraftProposalForNewFlow(); if (!draftId) { toast.error("Unable to create draft proposal."); return; } const response = await API.post(`/proposals/${draftId}/send/`, { to_email: lead?.email || null, monthly_service_charge: monthlyServiceCharge, monthly_service_name: selectedMonthlyCharge?.name || monthlyServiceLabel || "Monthly Monitoring Charge", monitoring_addon_name: monitoringAddonName || "", monitoring_addon_charge: monitoringAddonName ? monitoringAddonCharge : null, activation_fee: activationFee != null && Number(activationFee) > 0 ? Number(activationFee) : null, proposal_discount: proposalDiscount || 0, discount_name: selectedDiscount?.name || "Discount", message: "", special_terms: proposalSpecialTerms || "" }); if (response?.data?.fillquick_error) { toast.warning(response.data.fillquick_error); } setActiveProposalStatus("sent"); const leadRes = await API.get(`/leads/${lead.id}/`); setLead(leadRes.data); await loadLeadProposalsFromNewApi(lead.id); setProposalSentMapOpen(true); } catch (err) { console.error(err); const detail = err?.response?.data?.detail || ""; if (/recipient email/i.test(detail)) { setProposalEmailRequiredOpen(true); return; } toast.error(detail || "Failed to send proposal"); } }} sx={{ textTransform: "none", bgcolor: "#0874f9", fontWeight: 700, boxShadow: "0 6px 16px rgba(8,116,249,0.20)", "&:hover": { bgcolor: "#0667df" } }}>Send Proposal</Button>}</Stack> : null}
              </Stack>
            </CardContent>
          </Card>
        )}
{/* --------------------------------------------------Replace Equipment Package */}
<Dialog
  open={replacePackageOpen}
  onClose={closeReplacePackageDialog}
  maxWidth="xs"
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: "22px",
      overflow: "hidden",
      bgcolor: "background.paper",
      boxShadow: theme.palette.mode === "dark"
        ? "0 28px 70px rgba(0,0,0,0.52)"
        : "0 28px 70px rgba(15,23,42,0.24)",
      border: "1px solid",
      borderColor: theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.10)"
        : "rgba(15,23,42,0.06)",
    },
  }}
  slotProps={{
    backdrop: {
      sx: {
        bgcolor: "rgba(15,23,42,0.38)",
        backdropFilter: "blur(2px)",
      },
    },
  }}
>
  <Box sx={{ px: { xs: 2.5, sm: 3.25 }, pt: 3, pb: 2.5 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "0.68rem",
            fontWeight: 700,
            color: "text.secondary",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            mb: 0.75,
          }}
        >
          Replace Package
        </Typography>
        <Typography sx={{ fontSize: "1.28rem", fontWeight: 700, color: "text.primary", lineHeight: 1.25 }}>
          Replace equipment package?
        </Typography>
        <Typography sx={{ mt: 0.8, fontSize: "0.84rem", color: "text.secondary", lineHeight: 1.55 }}>
          Selecting <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
            {pendingReplacementPackage?.name || "this package"}
          </Box> will replace your current equipment package.
        </Typography>
      </Box>

      <IconButton
        onClick={closeReplacePackageDialog}
        disabled={replacingPackage}
        size="small"
        sx={{
          width: 40,
          height: 40,
          borderRadius: "12px",
          bgcolor: "action.hover",
          color: "text.secondary",
          flexShrink: 0,
          "&:hover": { bgcolor: "action.selected" },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Stack>
  </Box>

  <Divider />

  <Box sx={{ px: { xs: 2.5, sm: 3.25 }, py: 2 }}>
    <Box
      sx={{
        p: 1.6,
        borderRadius: "12px",
        bgcolor: theme.palette.mode === "dark"
          ? "rgba(245,158,11,0.10)"
          : "rgba(245,158,11,0.10)",
        border: "1px solid",
        borderColor: theme.palette.mode === "dark"
          ? "rgba(245,158,11,0.22)"
          : "rgba(245,158,11,0.18)",
      }}
    >
      <Typography sx={{ fontSize: "0.79rem", fontWeight: 700, color: theme.palette.mode === "dark" ? "#fbbf24" : "#b45309", lineHeight: 1.5 }}>
        Your current equipment will be removed.
      </Typography>
      <Typography sx={{ mt: 0.35, fontSize: "0.76rem", color: "text.secondary", lineHeight: 1.5 }}>
        This includes the existing kit and any individual products you've added.
      </Typography>
    </Box>
  </Box>

  <Divider />

  <Stack
    direction="row"
    justifyContent="flex-end"
    spacing={1.25}
    sx={{ px: { xs: 2.5, sm: 3.25 }, py: 2 }}
  >
    <Button
      onClick={closeReplacePackageDialog}
      disabled={replacingPackage}
      sx={{
        textTransform: "none",
        fontWeight: 600,
        color: "text.secondary",
        bgcolor: "action.hover",
        borderRadius: "12px",
        px: 2,
        minHeight: 44,
        "&:hover": { bgcolor: "action.selected" },
      }}
    >
      Cancel
    </Button>
    <Button
      variant="contained"
      onClick={confirmReplacePackage}
      disabled={replacingPackage}
      sx={{
        textTransform: "none",
        fontWeight: 700,
        borderRadius: "12px",
        px: 2.2,
        minHeight: 44,
        bgcolor: "#0874f9",
        boxShadow: "none",
        "&:hover": { bgcolor: "#0667df", boxShadow: "none" },
      }}
    >
      {replacingPackage ? "Replacing…" : "Replace Package"}
    </Button>
  </Stack>
</Dialog>

{/* --------------------------------------------------Proposal Quick View */}
<Dialog
  open={quickViewOpen}
  onClose={closeQuickView}
  maxWidth="md"
  fullWidth
>
  {/* Header */}
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      px: 2,
      py: 1,
      borderBottom: "1px solid",
      borderColor: "divider",
    }}
  >
    <Typography fontWeight={600}>
      Proposal Preview
    </Typography>
    <IconButton size="small" onClick={closeQuickView}>
      <CloseIcon fontSize="small" />
    </IconButton>
  </Box>

  {/* Content */}
  <Box sx={{ 
    height: { xs: "50vh", sm: "60vh", md: "80vh" },
    position: "relative",
    overflow: "hidden",
  }}>
    {quickViewLoading && (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: { xs: "50vh", sm: "60vh", md: "80vh" },
        }}
      >
        <CircularProgress />
      </Box>
    )}

    {!quickViewLoading && quickViewError && (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: { xs: "50vh", sm: "60vh", md: "80vh" },
          p: 3,
          textAlign: "center",
        }}
      >
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          Failed to load proposal preview
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Unable to display the proposal preview. Please try again later.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() =>
            lastPreviewProposal && openQuickView(lastPreviewProposal)
          }
        >
          Retry
        </Button>
      </Box>
    )}

    {!quickViewLoading && !quickViewError && quickViewHtml && (
      <Box
        sx={{
          height: "100%",
          overflow: "auto",
          WebkitOverflowScrolling: "touch",
          p: { xs: 1, sm: 2 },
        }}
      >
        <iframe
          srcDoc={quickViewHtml}
          sandbox="allow-same-origin allow-scripts"
          style={{
            width: "100%",
            minHeight: "100%",
            border: "none",
            transform: isMobile ? "scale(0.65)" : "none",
            transformOrigin: "top left",
            pointerEvents: "auto",
          }}
          title="Proposal Preview"
        />
      </Box>
    )}

    {!quickViewLoading && !quickViewError && !quickViewHtml && (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: { xs: "50vh", sm: "60vh", md: "80vh" },
          p: 3,
          textAlign: "center",
        }}
      >
        <Typography variant="body1" color="text.secondary">
          Proposal preview not available
        </Typography>
      </Box>
    )}
  </Box>
</Dialog>

      {/* --------------------------------------------------------Delete Confirmation */}
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title="Delete Lead"
        message={`Archive ${lead?.first_name} ${lead?.last_name}? They will be removed from active lists and can be restored later.`}
        confirmText="Yes, Delete"
        severity="error"
      />

      {/* ---------------------------------------------------------Restore Confirmation */}
      <ConfirmDialog
        open={confirmRestore}
        onClose={() => setConfirmRestore(false)}
        onConfirm={handleRestore}
        title="Restore Lead"
        message={`Are you sure you want to restore ${lead?.first_name} ${lead?.last_name}? This will make the lead active again.`}
        confirmText="Restore"
        severity="success"
      />

      <RequiredChoiceDialog
        open={proposalSentMapOpen}
        onClose={() => setProposalSentMapOpen(false)}
        eyebrow="Proposal"
        title="Your proposal was sent"
        message={
          cameFromMobileMap
            ? "The proposal was emailed. You can return to this lead on the map."
            : "The proposal was emailed and stays open on this tab."
        }
        actionLabel={cameFromMobileMap ? "Return to lead map" : "OK"}
        zIndex={1400}
        onAction={() => {
          setProposalSentMapOpen(false);
          if (cameFromMobileMap) goBack();
        }}
      />

      <RequiredChoiceDialog
        open={followUpDateRequiredOpen}
        onClose={() => setFollowUpDateRequiredOpen(false)}
        eyebrow="Save lead"
        title="Follow-up date required"
        message="Choose a follow-up date before saving this lead."
        actionLabel="Choose date"
        zIndex={1400}
        onAction={() => {
          setFollowUpDateRequiredOpen(false);
          focusRequiredField("lead-detail-follow-up-date");
        }}
      />

      <Dialog
        open={proposalEmailRequiredOpen}
        onClose={() => setProposalEmailRequiredOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "22px",
            mx: { xs: 2, sm: 0 },
            bgcolor: "background.paper",
            boxShadow: theme.palette.mode === "dark"
              ? "0 28px 70px rgba(0,0,0,0.52)"
              : "0 28px 70px rgba(15,23,42,0.24)",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              bgcolor: "rgba(15,23,42,0.38)",
              backdropFilter: "blur(2px)",
            },
          },
        }}
      >
        <Box sx={{ px: { xs: 2.5, sm: 3.25 }, pt: 2.5, pb: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  color: "text.secondary",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  mb: 0.75,
                }}
              >
                Send proposal
              </Typography>
              <Typography sx={{ fontSize: { xs: "1.2rem", sm: "1.28rem" }, fontWeight: 700, lineHeight: 1.25 }}>
                Customer email required
              </Typography>
              <Typography sx={{ mt: 0.9, fontSize: "0.86rem", color: "text.secondary", lineHeight: 1.55 }}>
                Add the customer's email on this lead before sending the proposal.
              </Typography>
            </Box>
            <IconButton
              onClick={() => setProposalEmailRequiredOpen(false)}
              aria-label="Close"
              size="small"
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                bgcolor: "action.hover",
                color: "text.secondary",
                flexShrink: 0,
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
        <Divider />
        <Stack
          direction={{ xs: "column-reverse", sm: "row" }}
          justifyContent="flex-end"
          spacing={1.25}
          sx={{ px: { xs: 2.5, sm: 3.25 }, py: 2 }}
        >
          <Button
            onClick={() => setProposalEmailRequiredOpen(false)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "text.secondary",
              bgcolor: "action.hover",
              borderRadius: "12px",
              px: 2,
              minHeight: 44,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddProposalEmail}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "12px",
              px: 2,
              minHeight: 44,
              bgcolor: "#0874f9",
              boxShadow: "none",
              "&:hover": { bgcolor: "#0667df", boxShadow: "none" },
            }}
          >
            Add email
          </Button>
        </Stack>
      </Dialog>

      <ConfirmDialog
        open={confirmWithdrawOpen}
        onClose={() => {
          setConfirmWithdrawOpen(false);
          setWithdrawProposalId(null);
        }}
        onConfirm={handleWithdrawProposal}
        title="Withdraw proposal?"
        message="Withdraw this proposal? The customer link will stop working."
        confirmText="Withdraw"
        severity="error"
      />
      </Box>
      </Box>
    </Box>
  );
}